/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useMemo, useRef, Suspense } from 'react';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { CalculatorType } from './types';
import { 
  Search, 
  ChevronRight, 
  ChevronLeft, 
  CheckCircle, 
  Sparkles, 
  TrendingUp, 
  BookOpen, 
  HelpCircle, 
  ExternalLink,
  Users,
  Award,
  Calendar,
  Briefcase,
  Layers,
  Shield,
  Heart,
  Settings,
  Calculator,
  X,
  Code,
  ArrowRight
} from 'lucide-react';

import { 
  CATEGORIES_DATA, 
  RATGEBER_HIGHLIGHTS, 
  INFOCENTER_HIGHLIGHTS, 
  STATISTIK_HIGHLIGHTS, 
  TOP_CALCULATORS,
  CalculatorItem
} from './data/calculatorsData';
import { SmartRechnrIllustration } from './components/SmartRechnrIllustration';

// Lazy load heavy components for optimized load time, reduced bundle size, and improved Lighthouse scores
const ImpressumPage = React.lazy(() => import('./components/legal/impress').then(m => ({ default: m.ImpressumPage })));
const DatenschutzPage = React.lazy(() => import('./components/legal/datens').then(m => ({ default: m.DatenschutzPage })));
const DisclaimerPage = React.lazy(() => import('./components/legal/haftung').then(m => ({ default: m.DisclaimerPage })));
const KontaktPage = React.lazy(() => import('./components/legal/kontakt').then(m => ({ default: m.KontaktPage })));

const SeveranceCalculator = React.lazy(() => import('./components/arbeit-soziales/SeveranceCalculator').then(m => ({ default: m.SeveranceCalculator })));
const BruttoNettoCalculator = React.lazy(() => import('./components/steuer-rechner/BruttoNettoCalculator').then(m => ({ default: m.BruttoNettoCalculator })));
const BruttoRechnerCalculator = React.lazy(() => import('./components/steuer-rechner/BruttoRechnerCalculator').then(m => ({ default: m.BruttoRechnerCalculator })));
const EinkommensteuerCalculator = React.lazy(() => import('./components/steuer-rechner/EinkommensteuerCalculator').then(m => ({ default: m.EinkommensteuerCalculator })));
const LohnsteuerCalculator = React.lazy(() => import('./components/steuer-rechner/LohnsteuerCalculator').then(m => ({ default: m.LohnsteuerCalculator })));
const NettopreisCalculator = React.lazy(() => import('./components/steuer-rechner/NettopreisCalculator').then(m => ({ default: m.NettopreisCalculator })));
const AbgeltungssteuerCalculator = React.lazy(() => import('./components/steuer-rechner/AbgeltungssteuerCalculator').then(m => ({ default: m.AbgeltungssteuerCalculator })));
const AussergewoehnlicheBelastungenCalculator = React.lazy(() => import('./components/steuer-rechner/AussergewoehnlicheBelastungenCalculator').then(m => ({ default: m.AussergewoehnlicheBelastungenCalculator })));
const SteuerrechnerCalculator = React.lazy(() => import('./components/steuer-rechner/SteuerrechnerCalculator').then(m => ({ default: m.SteuerrechnerCalculator })));
const ErbschaftsteuerCalculator = React.lazy(() => import('./components/steuer-rechner/ErbschaftsteuerCalculator').then(m => ({ default: m.ErbschaftsteuerCalculator })));
const ErbfolgeCalculator = React.lazy(() => import('./components/recht/ErbfolgeCalculator').then(m => ({ default: m.ErbfolgeCalculator })));
const ArbeitslosengeldCalculator = React.lazy(() => import('./components/arbeit-soziales/ArbeitslosengeldCalculator').then(m => ({ default: m.ArbeitslosengeldCalculator })));
const BafoegCalculator = React.lazy(() => import('./components/arbeit-soziales/BafoegCalculator').then(m => ({ default: m.BafoegCalculator })));
const WohngeldCalculator = React.lazy(() => import('./components/arbeit-soziales/WohngeldCalculator').then(m => ({ default: m.WohngeldCalculator })));
const BuergergeldCalculator = React.lazy(() => import('./components/arbeit-soziales/BuergergeldCalculator').then(m => ({ default: m.BuergergeldCalculator })));
const ElterngeldCalculator = React.lazy(() => import('./components/arbeit-soziales/ElterngeldCalculator').then(m => ({ default: m.ElterngeldCalculator })));
const Elternzeitrechner = React.lazy(() => import('./components/arbeit-soziales/Elternzeitrechner').then(m => ({ default: m.Elternzeitrechner })));
const Arbeitszeitrechner = React.lazy(() => import('./components/zeit-datum/Arbeitszeitrechner').then(m => ({ default: m.Arbeitszeitrechner })));
const AltersrechnerCalculator = React.lazy(() => import('./components/zeit-datum/AltersrechnerCalculator').then(m => ({ default: m.AltersrechnerCalculator })));
const ArbeitstageRechner = React.lazy(() => import('./components/zeit-datum/ArbeitstageRechner').then(m => ({ default: m.ArbeitstageRechner })));
const ZeitrechnerCalculator = React.lazy(() => import('./components/zeit-datum/ZeitrechnerCalculator').then(m => ({ default: m.ZeitrechnerCalculator })));
const DatumsrechnerCalculator = React.lazy(() => import('./components/zeit-datum/DatumsrechnerCalculator').then(m => ({ default: m.DatumsrechnerCalculator })));
const EtRechner = React.lazy(() => import('./components/gesundheit/EtRechner').then(m => ({ default: m.EtRechner })));
const SswRechner = React.lazy(() => import('./components/gesundheit/SswRechner').then(m => ({ default: m.SswRechner })));
const EtFelgenRechner = React.lazy(() => import('./components/umrechner/EtFelgenRechner').then(m => ({ default: m.EtFelgenRechner })));
const RestwertRechner = React.lazy(() => import('./components/kredit-finanzierung/RestwertRechner').then(m => ({ default: m.RestwertRechner })));
const EisprungRechner = React.lazy(() => import('./components/gesundheit/Eisprungrechner').then(m => ({ default: m.Eisprungrechner })));
const BmiRechner = React.lazy(() => import('./components/gesundheit/BmiRechner').then(m => ({ default: m.BmiRechner })));
const BmiKinderRechner = React.lazy(() => import('./components/gesundheit/BmiKinderRechner').then(m => ({ default: m.BmiKinderRechner })));
const Laengenumrechner = React.lazy(() => import('./components/umrechner/Laengenumrechner').then(m => ({ default: m.Laengenumrechner })));

// Dedicated TSX components for each individual Feiertage & Brückentage page for full SEO & code splitting
const Feiertage2026 = React.lazy(() => import('./components/feiertagecategory/Feiertage2026').then(m => ({ default: m.Feiertage2026 })));
const FeiertageInDeutschland = React.lazy(() => import('./components/feiertagecategory/FeiertageInDeutschland').then(m => ({ default: m.FeiertageInDeutschland })));
const Feiertage2027 = React.lazy(() => import('./components/feiertagecategory/Feiertage2027').then(m => ({ default: m.Feiertage2027 })));
const FeiertageAll = React.lazy(() => import('./components/feiertagecategory/FeiertageAll').then(m => ({ default: m.FeiertageAll })));
const Feiertage2025 = React.lazy(() => import('./components/feiertagecategory/Feiertage2025').then(m => ({ default: m.Feiertage2025 })));
const FeiertageNrw2026 = React.lazy(() => import('./components/feiertagecategory/FeiertageNrw2026').then(m => ({ default: m.FeiertageNrw2026 })));
const Feiertage2026Nrw = React.lazy(() => import('./components/feiertagecategory/Feiertage2026Nrw').then(m => ({ default: m.Feiertage2026Nrw })));
const NrwFeiertage2026 = React.lazy(() => import('./components/feiertagecategory/NrwFeiertage2026').then(m => ({ default: m.NrwFeiertage2026 })));
const FeiertageNrw2025 = React.lazy(() => import('./components/feiertagecategory/FeiertageNrw2025').then(m => ({ default: m.FeiertageNrw2025 })));
const FeiertageBayern2026 = React.lazy(() => import('./components/feiertagecategory/FeiertageBayern2026').then(m => ({ default: m.FeiertageBayern2026 })));
const FeiertageBayern2025 = React.lazy(() => import('./components/feiertagecategory/FeiertageBayern2025').then(m => ({ default: m.FeiertageBayern2025 })));
const FeiertageBw2026 = React.lazy(() => import('./components/feiertagecategory/FeiertageBw2026').then(m => ({ default: m.FeiertageBw2026 })));
const FeiertageBadenWuerttemberg2026 = React.lazy(() => import('./components/feiertagecategory/FeiertageBadenWuerttemberg2026').then(m => ({ default: m.FeiertageBadenWuerttemberg2026 })));
const FeiertageHessen2026 = React.lazy(() => import('./components/feiertagecategory/FeiertageHessen2026').then(m => ({ default: m.FeiertageHessen2026 })));
const FeiertageNiedersachsen2026 = React.lazy(() => import('./components/feiertagecategory/FeiertageNiedersachsen2026').then(m => ({ default: m.FeiertageNiedersachsen2026 })));
const FeiertageBerlin2026 = React.lazy(() => import('./components/feiertagecategory/FeiertageBerlin2026').then(m => ({ default: m.FeiertageBerlin2026 })));
const FeiertageSachsen2026 = React.lazy(() => import('./components/feiertagecategory/FeiertageSachsen2026').then(m => ({ default: m.FeiertageSachsen2026 })));
const FeiertageSachsen2025 = React.lazy(() => import('./components/feiertagecategory/FeiertageSachsen2025').then(m => ({ default: m.FeiertageSachsen2025 })));
const FeiertageRlp2026 = React.lazy(() => import('./components/feiertagecategory/FeiertageRlp2026').then(m => ({ default: m.FeiertageRlp2026 })));
const FeiertageHamburg2026 = React.lazy(() => import('./components/feiertagecategory/FeiertageHamburg2026').then(m => ({ default: m.FeiertageHamburg2026 })));
const FeiertageMai2026 = React.lazy(() => import('./components/feiertagecategory/FeiertageMai2026').then(m => ({ default: m.FeiertageMai2026 })));
const BrueckentagePlaner = React.lazy(() => import('./components/feiertagecategory/BrueckentagePlaner').then(m => ({ default: m.BrueckentagePlaner })));

// 6 Neue Energie-Rechner 2027
const Gasverbrauchsrechner = React.lazy(() => import('./components/energie/Gasverbrauchsrechner').then(m => ({ default: m.Gasverbrauchsrechner })));
const LedWattRechner = React.lazy(() => import('./components/energie/LedWattRechner').then(m => ({ default: m.LedWattRechner })));
const Stromkostenrechner = React.lazy(() => import('./components/energie/Stromkostenrechner').then(m => ({ default: m.Stromkostenrechner })));
const Stromverbrauchsrechner = React.lazy(() => import('./components/energie/Stromverbrauchsrechner').then(m => ({ default: m.Stromverbrauchsrechner })));
const VoltWattAmpereRechner = React.lazy(() => import('./components/energie/VoltWattAmpereRechner').then(m => ({ default: m.VoltWattAmpereRechner })));
const WaermepumpenRechner = React.lazy(() => import('./components/energie/WaermepumpenRechner').then(m => ({ default: m.WaermepumpenRechner })));
const Waehrungsrechner = React.lazy(() => import('./components/geldanlage-sparen/Waehrungsrechner').then(m => ({ default: m.Waehrungsrechner })));

// 3 Neue Bank- & Geldanlage-Rechner 2027
const BankverbindungPruefen = React.lazy(() => import('./components/geldanlage-sparen/BankverbindungPruefen').then(m => ({ default: m.BankverbindungPruefen })));
const Bankleitzahlensuche = React.lazy(() => import('./components/geldanlage-sparen/Bankleitzahlensuche').then(m => ({ default: m.Bankleitzahlensuche })));
const EtfSparplanrechner = React.lazy(() => import('./components/geldanlage-sparen/EtfSparplanrechner').then(m => ({ default: m.EtfSparplanrechner })));
const Entnahmeplanrechner = React.lazy(() => import('./components/geldanlage-sparen/Entnahmeplanrechner').then(m => ({ default: m.Entnahmeplanrechner })));
const Bankrecherche = React.lazy(() => import('./components/geldanlage-sparen/Bankrecherche').then(m => ({ default: m.Bankrecherche })));
const IbanRechner = React.lazy(() => import('./components/geldanlage-sparen/IbanRechner').then(m => ({ default: m.IbanRechner })));

const FeiertageCalculator = React.lazy(() => import('./components/feiertagecategory/FeiertageCalculator').then(m => ({ default: m.FeiertageCalculator })));
const EmbedPage = React.lazy(() => import('./components/embed/EmbedPage').then(m => ({ default: m.EmbedPage })));
const NotFound = React.lazy(() => import('./components/NotFound').then(m => ({ default: m.NotFound })));

export default function App() {
  // Sync router with window.location.pathname for SEO & indexability
  const normalizePathName = (rawPath: string) => {
    let normalized = rawPath || '/';
    if (normalized.endsWith('.html')) {
      normalized = normalized.slice(0, -5);
    }
    if (normalized.endsWith('/index')) {
      normalized = normalized.slice(0, -6);
    }
    if (normalized !== '/' && normalized.endsWith('/')) {
      normalized = normalized.slice(0, -1);
      if (typeof window !== 'undefined' && window.location.pathname.endsWith('/') && window.location.pathname !== '/') {
        try {
          const cleanUrl = window.location.pathname.replace(/\/+$/, '') + window.location.search + window.location.hash;
          window.history.replaceState(null, '', cleanUrl);
        } catch (e) {}
      }
    }

    // Automatically normalize nested/category paths to the main short SEO paths
    if (normalized === '/arbeit-soziales/abfindungsrechner') {
      try {
        window.history.replaceState(null, '', '/abfindungsrechner');
      } catch (e) {}
      normalized = '/abfindungsrechner';
    } else if (normalized === '/steuerrechner/brutto-netto') {
      try {
        window.history.replaceState(null, '', '/brutto-netto');
      } catch (e) {}
      normalized = '/brutto-netto';
    } else if (
      normalized === '/steuerrechner/bruttorechner' ||
      normalized === '/steuerrechner/brutto-rechner' ||
      normalized === '/brutto-rechner' ||
      normalized === '/bruttorechner'
    ) {
      try {
        window.history.replaceState(null, '', '/bruttorechner');
      } catch (e) {}
      normalized = '/bruttorechner';
    } else if (normalized === '/steuerrechner/einkommensteuer') {
      try {
        window.history.replaceState(null, '', '/einkommensteuer');
      } catch (e) {}
      normalized = '/einkommensteuer';
    } else if (normalized === '/steuerrechner/lohnsteuerrechner') {
      try {
        window.history.replaceState(null, '', '/lohnsteuerrechner');
      } catch (e) {}
      normalized = '/lohnsteuerrechner';
    } else if (normalized === '/steuerrechner/nettopreis-rechner') {
      try {
        window.history.replaceState(null, '', '/nettopreis-rechner');
      } catch (e) {}
      normalized = '/nettopreis-rechner';
    } else if (normalized === '/steuerrechner/steuer-berechnen' || normalized === '/steuer-berechnen') {
      try {
        window.history.replaceState(null, '', '/steuer-berechnen');
      } catch (e) {}
      normalized = '/steuer-berechnen';
    } else if (
      normalized === '/steuerrechner/erbschaftsteuer-rechner' ||
      normalized === '/steuerrechner/erbschaftsteuerrechner' ||
      normalized === '/erbschaftsteuerrechner' ||
      normalized === '/erbschaftsteuer-rechner'
    ) {
      try {
        window.history.replaceState(null, '', '/erbschaftsteuer-rechner');
      } catch (e) {}
      normalized = '/erbschaftsteuer-rechner';
    } else if (
      normalized === '/steuerrechner/erbfolge-rechner' ||
      normalized === '/erbfolgerechner' ||
      normalized === '/erbfolge-rechner'
    ) {
      try {
        window.history.replaceState(null, '', '/erbfolge-rechner');
      } catch (e) {}
      normalized = '/erbfolge-rechner';
    } else if (normalized === '/arbeit-soziales/arbeitslosengeld-rechner' || normalized === '/arbeitslosengeld-rechner') {
      try {
        window.history.replaceState(null, '', '/arbeitslosengeld-rechner');
      } catch (e) {}
      normalized = '/arbeitslosengeld-rechner';
    } else if (normalized === '/arbeit-soziales/bafoeg-rechner' || normalized === '/bafoeg-rechner') {
      try {
        window.history.replaceState(null, '', '/bafoeg-rechner');
      } catch (e) {}
      normalized = '/bafoeg-rechner';
    } else if (normalized === '/arbeit-soziales/wohngeldrechner' || normalized === '/wohngeldrechner') {
      try {
        window.history.replaceState(null, '', '/wohngeldrechner');
      } catch (e) {}
      normalized = '/wohngeldrechner';
    } else if (normalized === '/arbeit-soziales/buergergeld-rechner' || normalized === '/buergergeld-rechner' || normalized === '/buergergeldrechner') {
      try {
        window.history.replaceState(null, '', '/buergergeld-rechner');
      } catch (e) {}
      normalized = '/buergergeld-rechner';
    } else if (normalized === '/arbeit-soziales/elterngeld-rechner' || normalized === '/elterngeld-rechner' || normalized === '/elterngeldrechner') {
      try {
        window.history.replaceState(null, '', '/elterngeld-rechner');
      } catch (e) {}
      normalized = '/elterngeld-rechner';
    } else if (normalized === '/arbeit-soziales/elternzeitrechner' || normalized === '/elternzeitrechner' || normalized === '/elternzeit') {
      try {
        window.history.replaceState(null, '', '/elternzeitrechner');
      } catch (e) {}
      normalized = '/elternzeitrechner';
    } else if (normalized === '/umrechner/laengenumrechner' || normalized === '/laengenumrechner') {
      try {
        window.history.replaceState(null, '', '/laengenumrechner');
      } catch (e) {}
      normalized = '/laengenumrechner';
    } else if (
      normalized === '/steuerrechner/abgeltungsteuerrechner' ||
      normalized === '/abgeltungsteuerrechner' ||
      normalized === '/steuerrechner/abgeltungssteuer-rechner' ||
      normalized === '/abgeltungssteuer-rechner' ||
      normalized === '/kapitalertragsteuer-rechner' ||
      normalized === '/steuerrechner/kapitalertragsteuer-rechner'
    ) {
      try {
        window.history.replaceState(null, '', '/abgeltungsteuerrechner');
      } catch (e) {}
      normalized = '/abgeltungsteuerrechner';
    } else if (
      normalized === '/steuerrechner/aussergewoehnliche-belastungen' ||
      normalized === '/aussergewoehnliche-belastungen' ||
      normalized === '/aussergewoehnliche-belastungen-rechner' ||
      normalized === '/steuerrechner/aussergewoehnliche-belastungen-rechner'
    ) {
      try {
        window.history.replaceState(null, '', '/aussergewoehnliche-belastungen');
      } catch (e) {}
      normalized = '/aussergewoehnliche-belastungen';
    } else if (normalized === '/arbeit-soziales/arbeitszeitrechner' || normalized === '/zeit-datum/arbeitszeitrechner' || normalized === '/arbeitszeitrechner') {
      try {
        window.history.replaceState(null, '', '/arbeitszeitrechner');
      } catch (e) {}
      normalized = '/arbeitszeitrechner';
    } else if (normalized === '/zeit-datum/altersrechner' || normalized === '/altersrechner' || normalized === '/alters-rechner') {
      try {
        window.history.replaceState(null, '', '/altersrechner');
      } catch (e) {}
      normalized = '/altersrechner';
    } else if (
      normalized === '/zeit-datum/arbeitstage-rechner' || 
      normalized === '/arbeitstage-rechner' || 
      normalized === '/arbeitstagerechner' || 
      normalized === '/zeit-datum/arbeitstagerechner'
    ) {
      try {
        window.history.replaceState(null, '', '/arbeitstage-rechner');
      } catch (e) {}
      normalized = '/arbeitstage-rechner';
    } else if (normalized === '/zeit-datum/zeitrechner' || normalized === '/zeitrechner') {
      try {
        window.history.replaceState(null, '', '/zeitrechner');
      } catch (e) {}
      normalized = '/zeitrechner';
    } else if (normalized === '/zeit-datum/datumsrechner' || normalized === '/datumsrechner') {
      try {
        window.history.replaceState(null, '', '/datumsrechner');
      } catch (e) {}
      normalized = '/datumsrechner';
    } else if (normalized === '/familie/et-rechner' || normalized === '/et-rechner' || normalized === '/geburtstermin' || normalized === '/geburtsterminrechner') {
      try {
        window.history.replaceState(null, '', '/et-rechner');
      } catch (e) {}
      normalized = '/et-rechner';
    } else if (normalized === '/familie/ssw-rechner' || normalized === '/ssw-rechner' || normalized === '/et-rechner-ssw' || normalized === '/schwangerschaftswochen-rechner') {
      try {
        window.history.replaceState(null, '', '/ssw-rechner');
      } catch (e) {}
      normalized = '/ssw-rechner';
    } else if (normalized === '/umrechner/et-rechner-felgen' || normalized === '/kfz/et-rechner-felgen' || normalized === '/et-rechner-felgen' || normalized === '/felgenrechner' || normalized === '/einpresstiefe-rechner') {
      try {
        window.history.replaceState(null, '', '/et-rechner-felgen');
      } catch (e) {}
      normalized = '/et-rechner-felgen';
    } else if (normalized === '/kredit-finanzierung/restwert-rechner' || normalized === '/kfz/restwert-rechner' || normalized === '/restwert-rechner' || normalized === '/auto-wertverlust-rechner' || normalized === '/fahrzeugwert-rechner' || normalized === '/autowert-rechner' || normalized === '/restwertrechner') {
      try {
        window.history.replaceState(null, '', '/restwert-rechner');
      } catch (e) {}
      normalized = '/restwert-rechner';
    } else if (normalized === '/familie/eisprungrechner' || normalized === '/eisprungrechner' || normalized === '/et-rechner-eisprung' || normalized === '/ovulationsrechner') {
      try {
        window.history.replaceState(null, '', '/eisprungrechner');
      } catch (e) {}
      normalized = '/eisprungrechner';
    } else if (normalized === '/feiertage-2026-nrw' || normalized === '/nrw-feiertage-2026') {
      try {
        window.history.replaceState(null, '', '/feiertage-nrw-2026');
      } catch (e) {}
      normalized = '/feiertage-nrw-2026';
    } else if (normalized === '/feiertage-baden-wuerttemberg-2026') {
      try {
        window.history.replaceState(null, '', '/feiertage-bw-2026');
      } catch (e) {}
      normalized = '/feiertage-bw-2026';
    } else if (normalized === '/feiertage-deutschland') {
      try {
        window.history.replaceState(null, '', '/feiertage-in-deutschland');
      } catch (e) {}
      normalized = '/feiertage-in-deutschland';
    } else if (normalized === '/energie/waermepumpe' || normalized === '/waermepumpen-rechner' || normalized === '/waermepumpe-rechner' || normalized === '/waermepumpe') {
      try {
        window.history.replaceState(null, '', '/waermepumpe');
      } catch (e) {}
      normalized = '/waermepumpe';
    } else if (normalized === '/energie/gasverbrauch' || normalized === '/gasverbrauchsrechner' || normalized === '/gasverbrauch-rechner' || normalized === '/gasverbrauch') {
      try {
        window.history.replaceState(null, '', '/gasverbrauch');
      } catch (e) {}
      normalized = '/gasverbrauch';
    } else if (normalized === '/energie/stromverbrauch' || normalized === '/stromverbrauchsrechner' || normalized === '/stromverbrauch-rechner' || normalized === '/stromverbrauch') {
      try {
        window.history.replaceState(null, '', '/stromverbrauch');
      } catch (e) {}
      normalized = '/stromverbrauch';
    } else if (normalized === '/energie/stromkosten' || normalized === '/stromkostenrechner' || normalized === '/stromkosten-rechner' || normalized === '/stromkosten') {
      try {
        window.history.replaceState(null, '', '/stromkosten');
      } catch (e) {}
      normalized = '/stromkosten';
    } else if (normalized === '/energie/led-watt' || normalized === '/led-watt-rechner' || normalized === '/led-watt-umrechner' || normalized === '/led-watt') {
      try {
        window.history.replaceState(null, '', '/led-watt');
      } catch (e) {}
      normalized = '/led-watt';
    } else if (normalized === '/energie/volt-watt-amp' || normalized === '/volt-watt-ampere-rechner' || normalized === '/volt-watt-ampere-converter' || normalized === '/volt-watt' || normalized === '/volt-watt-amp') {
      try {
        window.history.replaceState(null, '', '/volt-watt-amp');
      } catch (e) {}
      normalized = '/volt-watt-amp';
    } else if (
      normalized === '/geldanlage-sparen/waehrungsrechner' ||
      normalized === '/waehrungcategory/waehrungsrechner' ||
      normalized === '/waehrungsrechner' ||
      normalized === '/waehrungsumrechner' ||
      normalized === '/waehrungs-rechner' ||
      normalized === '/waehrung/waehrungsrechner'
    ) {
      try {
        window.history.replaceState(null, '', '/waehrungsrechner');
      } catch (e) {}
      normalized = '/waehrungsrechner';
    } else if (
      normalized === '/widget-generator' ||
      normalized === '/rechner-widget' ||
      normalized === '/widget' ||
      normalized === '/embed' ||
      normalized === '/rechner-einbetten' ||
      normalized === '/rechner-einbinden'
    ) {
      try {
        window.history.replaceState(null, '', '/rechner-einbinden');
      } catch (e) {}
      normalized = '/rechner-einbinden';
    } else if (
      normalized === '/bank-pruefen' ||
      normalized === '/bankverbindung-pruefen' ||
      normalized === '/bankverbindung' ||
      normalized === '/geldanlage-sparen/bank-pruefen' ||
      normalized === '/geldanlage-sparen/bankverbindung-pruefen'
    ) {
      try {
        window.history.replaceState(null, '', '/bankverbindung-pruefen');
      } catch (e) {}
      normalized = '/bankverbindung-pruefen';
    } else if (
      normalized === '/blz-suche' ||
      normalized === '/bankleitzahlensuche' ||
      normalized === '/bankleitzahlen-suche' ||
      normalized === '/bankleitzahl' ||
      normalized === '/geldanlage-sparen/blz-suche' ||
      normalized === '/geldanlage-sparen/bankleitzahlensuche'
    ) {
      try {
        window.history.replaceState(null, '', '/blz-suche');
      } catch (e) {}
      normalized = '/blz-suche';
    } else if (
      normalized === '/etf-sparplan' ||
      normalized === '/etf-sparplanrechner' ||
      normalized === '/etf-rechner' ||
      normalized === '/geldanlage-sparen/etf-sparplan' ||
      normalized === '/geldanlage-sparen/etf-sparplanrechner'
    ) {
      try {
        window.history.replaceState(null, '', '/etf-sparplanrechner');
      } catch (e) {}
      normalized = '/etf-sparplanrechner';
    } else if (
      normalized === '/entnahmeplan' ||
      normalized === '/entnahmeplanrechner' ||
      normalized === '/etf-entnahmeplan' ||
      normalized === '/geldanlage-sparen/entnahmeplan' ||
      normalized === '/geldanlage-sparen/entnahmeplanrechner'
    ) {
      try {
        window.history.replaceState(null, '', '/entnahmeplanrechner');
      } catch (e) {}
      normalized = '/entnahmeplanrechner';
    } else if (
      normalized === '/bankrecherche' ||
      normalized === '/banksuche' ||
      normalized === '/bank-suche' ||
      normalized === '/geldanlage-sparen/bankrecherche' ||
      normalized === '/geldanlage-sparen/banksuche'
    ) {
      try {
        window.history.replaceState(null, '', '/bankrecherche');
      } catch (e) {}
      normalized = '/bankrecherche';
    } else if (
      normalized === '/iban-rechner' ||
      normalized === '/iban-berechnen' ||
      normalized === '/iban-generator' ||
      normalized === '/geldanlage-sparen/iban-rechner' ||
      normalized === '/geldanlage-sparen/iban-berechnen'
    ) {
      try {
        window.history.replaceState(null, '', '/iban-rechner');
      } catch (e) {}
      normalized = '/iban-rechner';
    } else if (
      normalized === '/bmi-rechner' ||
      normalized === '/gesundheit/bmi-rechner' ||
      normalized === '/bmi'
    ) {
      try {
        window.history.replaceState(null, '', '/bmi-rechner');
      } catch (e) {}
      normalized = '/bmi-rechner';
    } else if (
      normalized === '/bmi-rechner-kinder' ||
      normalized === '/bmi-kinder' ||
      normalized === '/gesundheit/bmi-rechner-kinder' ||
      normalized === '/gesundheit/bmi-kinder' ||
      normalized === '/bmi-kind'
    ) {
      try {
        window.history.replaceState(null, '', '/bmi-rechner-kinder');
      } catch (e) {}
      normalized = '/bmi-rechner-kinder';
    } else if (
      normalized === '/eisprungrechner' ||
      normalized === '/gesundheit/eisprungrechner' ||
      normalized === '/familie/eisprungrechner' ||
      normalized === '/et-rechner-eisprung' ||
      normalized === '/ovulationsrechner'
    ) {
      try {
        window.history.replaceState(null, '', '/eisprungrechner');
      } catch (e) {}
      normalized = '/eisprungrechner';
    } else if (
      normalized === '/et-rechner' ||
      normalized === '/gesundheit/et-rechner' ||
      normalized === '/familie/et-rechner' ||
      normalized === '/geburtstermin' ||
      normalized === '/geburtsterminrechner'
    ) {
      try {
        window.history.replaceState(null, '', '/et-rechner');
      } catch (e) {}
      normalized = '/et-rechner';
    } else if (
      normalized === '/ssw-rechner' ||
      normalized === '/gesundheit/ssw-rechner' ||
      normalized === '/familie/ssw-rechner' ||
      normalized === '/et-rechner-ssw' ||
      normalized === '/schwangerschaftswochen-rechner'
    ) {
      try {
        window.history.replaceState(null, '', '/ssw-rechner');
      } catch (e) {}
      normalized = '/ssw-rechner';
    }

    return normalized || '/';
  };

  const getInitialPath = () => {
    if (typeof window !== 'undefined') {
      if (window.location.hash && window.location.hash.startsWith('#/')) {
        return normalizePathName(window.location.hash.substring(1));
      }
      return normalizePathName(window.location.pathname || '/');
    }
    return '/';
  };

  const [currentPath, setCurrentPath] = useState<string>(getInitialPath);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  
  // Track slider/carousel indices for each category independently
  const [categorySliders, setCategorySliders] = useState<Record<string, number>>({});
  const [feiertageSiloTab, setFeiertageSiloTab] = useState<'all' | 'jahr' | 'bundesland' | 'spezial'>('all');

  // Close live search dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsSearchOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const handleLocationChange = () => {
      let path = window.location.pathname || '/';
      if (path === '/' && window.location.hash && window.location.hash.startsWith('#/')) {
        path = window.location.hash.substring(1);
      }
      const normalized = normalizePathName(path);
      setCurrentPath(normalized);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('hashchange', handleLocationChange);
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('hashchange', handleLocationChange);
    };
  }, []);

  // Dynamically update SEO head elements (Canonical link, Meta description, Title) on route change
  useEffect(() => {
    let title = "Smart Rechner 2026: Steuern, Gehalt & Finanzen berechnen";
    let description = "Kostenlose Online-Rechner für Deutschland: Brutto-Netto, Steuern, Bürgergeld, Wohngeld & BAföG 2026 exakt, anonym und rechtssicher online berechnen.";
    let canonical = currentPath && currentPath !== '/' ? `https://smart-rechnr.de${currentPath}` : "https://smart-rechnr.de/";

    if (currentPath === '/brutto-netto' || currentPath === '/steuerrechner/brutto-netto') {
      title = "Brutto-Netto-Rechner 2026 – Nettogehalt exakt berechnen";
      description = "Berechnen Sie Ihr Nettogehalt 2026 exakt. ✓ Inklusive aller Steuerklassen, Krankenkassen-Zusatzbeitrag & Freibeträge. Jetzt Gehalt gratis ermitteln!";
      canonical = "https://smart-rechnr.de/brutto-netto";
    } else if (
      currentPath === '/bruttorechner' ||
      currentPath === '/brutto-rechner' ||
      currentPath === '/steuerrechner/bruttorechner' ||
      currentPath === '/steuerrechner/brutto-rechner'
    ) {
      title = "Brutto-Rechner 2026 – Wunsch-Netto zu Brutto berechnen";
      description = "Wunsch-Netto zu Brutto berechnen: Ermitteln Sie das nötige Bruttogehalt für Ihr Wunscheinkommen 2026. Ideal für Gehaltsverhandlung & Jobwechsel!";
      canonical = "https://smart-rechnr.de/bruttorechner";
    } else if (currentPath === '/lohnsteuerrechner' || currentPath === '/steuerrechner/lohnsteuerrechner') {
      title = "Lohnsteuerrechner 2026 – Lohnsteuer & Abgaben ermitteln";
      description = "Ermitteln Sie Ihre Lohnsteuer 2026 online. ✓ Offizieller PAP des Bundesfinanzministeriums ✓ Inklusive Solidaritätszuschlag & Kirchensteuer. Kostenlos!";
      canonical = "https://smart-rechnr.de/lohnsteuerrechner";
    } else if (currentPath === '/einkommensteuer' || currentPath === '/steuerrechner/einkommensteuer') {
      title = "Einkommensteuer-Rechner 2026 – Steuer online berechnen";
      description = "Einkommensteuer 2026 kostenlos berechnen. ✓ Inklusive Grundfreibetrag, Splittingtarif, Fünftelregelung bei Abfindung & Progression. Jetzt berechnen!";
      canonical = "https://smart-rechnr.de/einkommensteuer";
    } else if (currentPath === '/abfindungsrechner' || currentPath === '/arbeit-soziales/abfindungsrechner') {
      title = "Abfindungsrechner 2026 – Fünftelregelung & Netto-Abfindung berechnen";
      description = "Abfindungsrechner 2026 / 2027: Abfindung netto berechnen nach § 34 EStG (Fünftelregelung), Steuerersparnis & Kündigungsabfindung kostenlos ermitteln.";
      canonical = "https://smart-rechnr.de/abfindungsrechner";
    } else if (currentPath === '/nettopreis-rechner' || currentPath === '/steuerrechner/nettopreis-rechner') {
      title = "Nettopreis-Rechner – Netto & Brutto mit MwSt berechnen";
      description = "Nettopreise aus Bruttopreisen oder umgekehrt sofort berechnen. ✓ Inklusive 19% & 7% Mehrwertsteuer sowie individueller Steuersätze. Schnell & exakt!";
      canonical = "https://smart-rechnr.de/nettopreis-rechner";
    } else if (
      currentPath === '/abgeltungsteuerrechner' ||
      currentPath === '/abgeltungssteuer-rechner' ||
      currentPath === '/steuerrechner/abgeltungsteuerrechner' ||
      currentPath === '/steuerrechner/abgeltungssteuer-rechner' ||
      currentPath === '/kapitalertragsteuer-rechner' ||
      currentPath === '/steuerrechner/kapitalertragsteuer-rechner'
    ) {
      title = "Abgeltungsteuerrechner 2026 – Kapitalertragsteuer online";
      description = "Abgeltungssteuer auf Aktien, ETFs & Zinsen 2026 berechnen. ✓ Inklusive Sparer-Pauschbetrag (1.000 €), Teilfreistellung, Kirchensteuer & Soli. Gratis!";
      canonical = "https://smart-rechnr.de/abgeltungsteuerrechner";
    } else if (
      currentPath === '/aussergewoehnliche-belastungen' ||
      currentPath === '/steuerrechner/aussergewoehnliche-belastungen' ||
      currentPath === '/aussergewoehnliche-belastungen-rechner' ||
      currentPath === '/steuerrechner/aussergewoehnliche-belastungen-rechner'
    ) {
      title = "Außergewöhnliche Belastungen Rechner – Zumutbare Belastung";
      description = "Zumutbare Belastung nach § 33 EStG berechnen: Ermitteln Sie steuerlich absetzbare Krankheits-, Pflege- & Bestattungskosten. Inklusive PDF-Export!";
      canonical = "https://smart-rechnr.de/aussergewoehnliche-belastungen";
    } else if (currentPath === '/steuer-berechnen' || currentPath === '/steuerrechner/steuer-berechnen') {
      title = "Steuerrechner 2026 – Deutsche Steuern kostenlos berechnen";
      description = "Alle Steuern in einem Tool: Einkommensteuer, Lohnsteuer, Soli und Mehrwertsteuer 2026 präzise berechnen. Schnell, anonym und rechtssicher online!";
      canonical = "https://smart-rechnr.de/steuer-berechnen";
    } else if (
      currentPath === '/erbschaftsteuer-rechner' ||
      currentPath === '/erbschaftsteuerrechner' ||
      currentPath === '/steuerrechner/erbschaftsteuer-rechner'
    ) {
      title = "Erbschaftsteuer-Rechner 2026 – Steuer & Freibetrag online";
      description = "Erbschaftsteuer & Schenkungsteuer 2026 berechnen. ✓ Freibeträge der Steuerklassen I-III ✓ Familienheim-Befreiung (§ 13) ✓ Netto-Erbe exakt ermitteln!";
      canonical = "https://smart-rechnr.de/erbschaftsteuer-rechner";
    } else if (
      currentPath === '/erbfolge-rechner' ||
      currentPath === '/erbfolgerechner' ||
      currentPath === '/steuerrechner/erbfolge-rechner'
    ) {
      title = "Gesetzliche Erbfolge Rechner – Erbe ohne Testament berechnen";
      description = "Gesetzliche Erbfolge ohne Testament berechnen: Erbanteile von Ehepartnern und Kindern ermitteln, Schaubild ansehen und deutsches Erbrecht verstehen.";
      canonical = "https://smart-rechnr.de/erbfolge-rechner";
    } else if (currentPath === '/arbeitslosengeld-rechner' || currentPath === '/arbeit-soziales/arbeitslosengeld-rechner') {
      title = "Arbeitslosengeld Rechner 2026 – ALG 1 Höhe, Dauer & Sperrzeit";
      description = "Arbeitslosengeld Rechner 2026 / 2027: Berechnen Sie Ihr Arbeitslosengeld 1 (ALG 1), Auszahlungshöhe (60% / 67%), Bezugsdauer (6–24 Monate) und Sperrzeit nach §§ 147–153 SGB III kostenlos & anonym.";
      canonical = "https://smart-rechnr.de/arbeitslosengeld-rechner";
    } else if (currentPath === '/bafoeg-rechner' || currentPath === '/arbeit-soziales/bafoeg-rechner') {
      title = "BAföG-Rechner 2026 – BAföG-Anspruch kostenlos berechnen";
      description = "BAföG-Anspruch 2026 für Studenten & Schüler berechnen. ✓ Inklusive Vermögensanrechnung, Freibeträgen für Eltern und Geschwister. Jetzt gratis ermitteln!";
      canonical = "https://smart-rechnr.de/bafoeg-rechner";
    } else if (currentPath === '/wohngeldrechner' || currentPath === '/arbeit-soziales/wohngeldrechner') {
      title = "Wohngeldrechner 2026 – Wohngeld-Plus Anspruch berechnen";
      description = "Wohngeld-Plus Anspruch 2026 online ermitteln. ✓ Inklusive Heizkosten- & Klimakomponente sowie offizieller Mietstufen aller Städte in Deutschland!";
      canonical = "https://smart-rechnr.de/wohngeldrechner";
    } else if (currentPath === '/buergergeld-rechner' || currentPath === '/arbeit-soziales/buergergeld-rechner') {
      title = "Bürgergeld-Rechner 2026 – Regelsatz & Anspruch berechnen";
      description = "Bürgergeld-Anspruch 2026 online berechnen: Regelsätze, Mehrbedarfe, Freibeträge auf Einkommen und Kosten für Miete & Heizung exakt ermitteln. Kostenlos!";
      canonical = "https://smart-rechnr.de/buergergeld-rechner";
    } else if (currentPath === '/elterngeld-rechner' || currentPath === '/arbeit-soziales/elterngeld-rechner') {
      title = "Elterngeldrechner 2026 – Elterngeld & Basis-Monate online";
      description = "Elterngeld 2026 online berechnen. ✓ Inklusive Basiselterngeld, ElterngeldPlus, Geschwisterbonus, Mehrlingszuschlag & Partnermonaten. Schnell & gratis!";
      canonical = "https://smart-rechnr.de/elterngeld-rechner";
    } else if (currentPath === '/elternzeitrechner' || currentPath === '/arbeit-soziales/elternzeitrechner') {
      title = "Elternzeitrechner 2026 – Fristen & Elternzeit berechnen";
      description = "Elternzeiträume, Mutterschutz und Anmeldefristen beim Arbeitgeber nach §§ 15/17 BEEG berechnen. Inklusive Resturlaubs-Ermittlung. Jetzt kostenlos!";
      canonical = "https://smart-rechnr.de/elternzeitrechner";
    } else if (currentPath === '/laengenumrechner' || currentPath === '/umrechner/laengenumrechner') {
      title = "Längen-Umrechner – Zoll, Inch in cm online umrechnen";
      description = "Zoll (Inch) exakt in Zentimeter (cm) und Meter umrechnen. ✓ Inklusive Umrechnungstabelle von 1 bis 300 Zoll, TV-Größenberater und Bruch-Darstellung!";
      canonical = "https://smart-rechnr.de/laengenumrechner";
    } else if (currentPath === '/arbeitszeitrechner' || currentPath === '/arbeit-soziales/arbeitszeitrechner' || currentPath === '/zeit-datum/arbeitszeitrechner') {
      title = "Arbeitszeitrechner 2026 – Tägliche Arbeitszeit & Pause";
      description = "Tägliche Netto-Arbeitszeit exakt berechnen. ✓ Pausenprüfung nach § 4 ArbZG ✓ Nachtschicht über Mitternacht ✓ Überstunden & Verdienst. Jetzt berechnen!";
      canonical = "https://smart-rechnr.de/arbeitszeitrechner";
    } else if (currentPath === '/altersrechner' || currentPath === '/zeit-datum/altersrechner' || currentPath === '/alters-rechner') {
      title = "Altersrechner – Genaues Alter, Tage & Sekunden berechnen";
      description = "Berechnen Sie Ihr exaktes Alter in Jahren, Monaten, Tagen und Sekunden. ✓ BGB-konform nach §§ 187/188 ✓ Schaltjahre ✓ Wochentag der Geburt. Gratis!";
      canonical = "https://smart-rechnr.de/altersrechner";
    } else if (
      currentPath === '/arbeitstage-rechner' || 
      currentPath === '/arbeitstagerechner' || 
      currentPath === '/zeit-datum/arbeitstage-rechner' ||
      currentPath === '/zeit-datum/arbeitstagerechner'
    ) {
      title = "Arbeitstage-Rechner 2026 – Werktage & Feiertage ermitteln";
      description = "Arbeitstage für 2026 exakt nach Bundesland berechnen. ✓ Gesetzliche Feiertage aller 16 Länder ✓ Urlaub & Brückentage ✓ Pendlerpauschale nach § 9 EStG.";
      canonical = "https://smart-rechnr.de/arbeitstage-rechner";
    } else if (currentPath === '/zeitrechner' || currentPath === '/zeit-datum/zeitrechner') {
      title = "Zeitrechner 2026 – Zeiten addieren & Zeitspanne ermitteln";
      description = "Stunden, Minuten und Sekunden exakt zusammenrechnen. Zeitspanne, Nachtschicht und Industrieminuten für die Lohnabrechnung schnell online ermitteln!";
      canonical = "https://smart-rechnr.de/zeitrechner";
    } else if (currentPath === '/datumsrechner' || currentPath === '/zeit-datum/datumsrechner') {
      title = "Datumsrechner 2026 – Datum berechnen & Tage addieren";
      description = "Tage, Wochen oder Monate zu einem Datum addieren oder abziehen. Inklusive Fristberechnung nach § 193 BGB, Werktagen und Schaltjahren. Jetzt testen!";
      canonical = "https://smart-rechnr.de/datumsrechner";
    } else if (currentPath === '/et-rechner' || currentPath === '/familie/et-rechner' || currentPath === '/geburtstermin' || currentPath === '/geburtsterminrechner') {
      title = "ET Rechner 2026 \u2013 Geburtstermin & Mutterschutz berechnen";
      description = "Geburtstermin nach Naegele-Regel, Eisprung oder Ultraschall berechnen. \u2713 Inklusive Mutterschutzfrist (\u00A7 3 MuSchG) und SSW-Fortschritt. Jetzt online!";
      canonical = "https://smart-rechnr.de/et-rechner";
    } else if (currentPath === '/ssw-rechner' || currentPath === '/familie/ssw-rechner' || currentPath === '/et-rechner-ssw' || currentPath === '/schwangerschaftswochen-rechner') {
      title = "SSW Rechner 2026 \u2013 Schwangerschaftswoche exakt berechnen";
      description = "Aktuelle SSW und Tag exakt berechnen. \u2713 Trimester-Fortschritt, Babygr\u00F6\u00DFe mit Frucht-Vergleich und Termine f\u00FCr den Mutterpass im \u00DCberblick. Kostenlos!";
      canonical = "https://smart-rechnr.de/ssw-rechner";
    } else if (currentPath === '/et-rechner-felgen' || currentPath === '/kfz/et-rechner-felgen' || currentPath === '/felgenrechner' || currentPath === '/einpresstiefe-rechner') {
      title = "ET Rechner Felgen \u2013 Einpresstiefe & Spurbreite berechnen";
      description = "Einpresstiefe (ET), Felgenbreite und Spurplatten f\u00FCr Autofelgen berechnen. Mit T\u00DCV 2%-Pr\u00FCfung, Felgenkanten-Verschiebung & Freig\u00E4ngigkeit. Gratis!";
      canonical = "https://smart-rechnr.de/et-rechner-felgen";
    } else if (currentPath === '/restwert-rechner' || currentPath === '/kfz/restwert-rechner' || currentPath === '/auto-wertverlust-rechner' || currentPath === '/fahrzeugwert-rechner' || currentPath === '/autowert-rechner') {
      title = "Restwert-Rechner – Auto-Wertverlust & Fahrzeugwert berechnen";
      description = "Restwert-Rechner: Kostenlos aktuellen Auto-Wert & Wertverlust nach Alter, km und Zustand berechnen. Inklusive Händler- und Privatverkaufspreis.";
      canonical = "https://smart-rechnr.de/restwert-rechner";
    } else if (currentPath === '/eisprungrechner' || currentPath === '/gesundheit/eisprungrechner' || currentPath === '/familie/eisprungrechner' || currentPath === '/et-rechner-eisprung' || currentPath === '/ovulationsrechner') {
      title = "Eisprungrechner 2027 – Fruchtbare Tage, Ovulation & Einnistung berechnen";
      description = "Kostenloser Eisprungrechner 2027: Fruchtbare Tage, Eisprung & Einnistung (Nidation) exakt berechnen. Mit interaktivem Zykluskalender, 3-Monats-Prognose & Früh-hCG-Terminen.";
      canonical = "https://smart-rechnr.de/eisprungrechner";
    } else if (currentPath === '/impressum') {
      title = "Impressum – Rechtliche Angaben & Anbieterkennzeichnung";
      description = "Impressum von smart-rechnr.de: Gesetzliche Angaben gemäß § 5 DDG / TMG, Kontaktdaten und rechtliche Hinweise zu unserem Online-Rechner-Portal.";
      canonical = "https://smart-rechnr.de/impressum";
    } else if (currentPath === '/datenschutz') {
      title = "Datenschutzerklärung – DSGVO & Schutz der Privatsphäre";
      description = "Datenschutzerklärung von smart-rechnr.de: Informationen zu Art, Umfang und Zweck der Datenverarbeitung sowie Ihren Rechten gemäß EU-DSGVO.";
      canonical = "https://smart-rechnr.de/datenschutz";
    } else if (currentPath === '/haftungsausschluss') {
      title = "Haftungsausschluss – Rechtliche Hinweise & Disclaimer";
      description = "Haftungsausschluss und Disclaimer für alle kostenlosen Berechnungen, mathematischen Formeln und redaktionellen Inhalte auf smart-rechnr.de.";
      canonical = "https://smart-rechnr.de/haftungsausschluss";
    } else if (currentPath === '/kontakt') {
      title = "Kontakt – Kundenservice, Fragen & Feedback einreichen";
      description = "Kontaktieren Sie das Team von smart-rechnr.de: Senden Sie uns Ihre Anfragen, Verbesserungsvorschläge oder Fragen zu unseren Online-Rechnern.";
      canonical = "https://smart-rechnr.de/kontakt";
    } else if (currentPath === '/feiertage-2026') {
      title = "Feiertage 2026 Deutschland – Gesetzliche Feiertage & Kalender";
      description = "Alle gesetzlichen Feiertage 2026 in Deutschland. Inklusive Brückentage-Planer, Wochentagen, Bundesland-Filter & iCal-Export.";
      canonical = "https://smart-rechnr.de/feiertage-2026";
    } else if (currentPath === '/feiertage-in-deutschland') {
      title = "Feiertage in Deutschland – Gesetzliche Feiertage aller 16 Länder";
      description = "Übersicht aller gesetzlichen Feiertage in Deutschland. Bundesweite & landesspezifische Feiertage, Lohnfortzahlung (§ 2 EntgFG) & Kalender.";
      canonical = "https://smart-rechnr.de/feiertage-in-deutschland";
    } else if (currentPath === '/feiertage' || currentPath === '/feiertage/') {
      title = "Feiertage Deutschland – Gesetzliche Feiertage, Termine & Gesetze";
      description = "Alle gesetzlichen Feiertage in Deutschland im Überblick: Bundesweite Feiertage, Bundesländer-Ranking, Brückentage und Kalender-Export.";
      canonical = "https://smart-rechnr.de/feiertage";
    } else if (currentPath === '/feiertage-2027') {
      title = "Feiertage 2027 Deutschland – Gesetzliche Feiertage & Termine";
      description = "Alle Feiertage 2027 in Deutschland im Überblick. Gesetzliche Feiertage aller Bundesländer, Ostertermine, Christi Himmelfahrt & Pfingsten 2027.";
      canonical = "https://smart-rechnr.de/feiertage-2027";
    } else if (currentPath === '/feiertage-2025') {
      title = "Feiertage 2025 Deutschland – Gesetzliche Feiertage im Kalender";
      description = "Gesetzliche Feiertage für das Jahr 2025 in Deutschland. Offizielle Termine aller Bundesländer im praktischen Überblick.";
      canonical = "https://smart-rechnr.de/feiertage-2025";
    } else if (currentPath === '/feiertage-nrw-2026') {
      title = "Feiertage NRW 2026 – Gesetzliche Feiertage Nordrhein-Westfalen";
      description = "Alle 11 gesetzlichen Feiertage 2026 in Nordrhein-Westfalen (NRW). Fronleichnam, Allerheiligen, Brückentage & Entgeltfortzahlung online.";
      canonical = "https://smart-rechnr.de/feiertage-nrw-2026";
    } else if (currentPath === '/feiertage-nrw-2025') {
      title = "Feiertage NRW 2025 – Gesetzliche Feiertage Nordrhein-Westfalen";
      description = "Feiertage 2025 in NRW: Alle 11 gesetzlichen Feiertage für Nordrhein-Westfalen inklusive Terminen und Wochentagen.";
      canonical = "https://smart-rechnr.de/feiertage-nrw-2025";
    } else if (currentPath === '/feiertage-bayern-2026') {
      title = "Feiertage Bayern 2026 – Gesetzliche Feiertage im Freistaat";
      description = "Alle 13 gesetzlichen Feiertage 2026 in Bayern. Heilige Drei Könige, Fronleichnam, Mariä Himmelfahrt, Allerheiligen & Augsburger Friedensfest.";
      canonical = "https://smart-rechnr.de/feiertage-bayern-2026";
    } else if (currentPath === '/feiertage-bayern-2025') {
      title = "Feiertage Bayern 2025 – Feiertagskalender Freistaat Bayern";
      description = "Gesetzliche Feiertage 2025 in Bayern im Überblick: Termine, Wochentage und Sonderregelungen für katholische Gemeinden.";
      canonical = "https://smart-rechnr.de/feiertage-bayern-2025";
    } else if (currentPath === '/feiertage-bw-2026') {
      title = "Feiertage Baden-Württemberg (BW) 2026 – Gesetzliche Feiertage & Kalender";
      description = "Alle 12 gesetzlichen Feiertage 2026 in Baden-Württemberg (BW). Heilige Drei Könige, Fronleichnam, Allerheiligen & Brückentage.";
      canonical = "https://smart-rechnr.de/feiertage-bw-2026";
    } else if (currentPath === '/feiertage-hessen-2026') {
      title = "Feiertage Hessen 2026 – Gesetzliche Feiertage & Brückentage";
      description = "Gesetzliche Feiertage 2026 in Hessen: Fronleichnam, Tag der Arbeit, Pfingsten und Brückentage für den optimalen Urlaub.";
      canonical = "https://smart-rechnr.de/feiertage-hessen-2026";
    } else if (currentPath === '/feiertage-niedersachsen-2026') {
      title = "Feiertage Niedersachsen 2026 – Gesetzliche Feiertage NI";
      description = "Gesetzliche Feiertage 2026 in Niedersachsen. Reformationstag, Tag der Arbeit, Ostern & Himmelfahrt im übersichtlichen Kalender.";
      canonical = "https://smart-rechnr.de/feiertage-niedersachsen-2026";
    } else if (currentPath === '/feiertage-berlin-2026') {
      title = "Feiertage Berlin 2026 – Gesetzliche Feiertage inkl. Frauentag";
      description = "Alle 10 gesetzlichen Feiertage 2026 in Berlin. Inklusive Internationaler Frauentag (8. März), Brückentage und Feiertagskalender.";
      canonical = "https://smart-rechnr.de/feiertage-berlin-2026";
    } else if (currentPath === '/feiertage-sachsen-2026') {
      title = "Feiertage Sachsen 2026 – Feiertage inkl. Buß- und Bettag";
      description = "Gesetzliche Feiertage 2026 in Sachsen: Reformationstag, Buß- und Bettag, Ostern & Weihnachten mit aktuellen Wochentagen.";
      canonical = "https://smart-rechnr.de/feiertage-sachsen-2026";
    } else if (currentPath === '/feiertage-sachsen-2025') {
      title = "Feiertage Sachsen 2025 – Gesetzliche Feiertage Kalender";
      description = "Alle gesetzlichen Feiertage 2025 im Freistaat Sachsen mit Buß- und Bettag, Reformationstag und Brückentagen.";
      canonical = "https://smart-rechnr.de/feiertage-sachsen-2025";
    } else if (currentPath === '/feiertage-rlp-2026') {
      title = "Feiertage Rheinland-Pfalz 2026 – Gesetzliche Feiertage RLP";
      description = "Gesetzliche Feiertage 2026 in Rheinland-Pfalz (RLP). Fronleichnam, Allerheiligen, Brückentage & arbeitsfreie Tage online.";
      canonical = "https://smart-rechnr.de/feiertage-rlp-2026";
    } else if (currentPath === '/feiertage-hamburg-2026') {
      title = "Feiertage Hamburg 2026 – Gesetzliche Feiertage der Hansestadt";
      description = "Alle gesetzlichen Feiertage 2026 in Hamburg. Reformationstag, Karfreitag, Pfingsten & Christi Himmelfahrt im Überblick.";
      canonical = "https://smart-rechnr.de/feiertage-hamburg-2026";
    } else if (currentPath === '/feiertage-mai-2026') {
      title = "Feiertage Mai 2026 – 1. Mai, Christi Himmelfahrt & Pfingsten";
      description = "Feiertage im Mai 2026 in Deutschland: Tag der Arbeit (Freitag!), Christi Himmelfahrt und Pfingsten für maximale Brückentage nutzen.";
      canonical = "https://smart-rechnr.de/feiertage-mai-2026";
    } else if (currentPath === '/brueckentage-planer') {
      title = "Brückentage-Planer 2026 & 2027 – Urlaubstage verdoppeln";
      description = "Brückentage 2026 und 2027 schlau planen: Verdoppeln Sie Ihren Urlaub mit Christi Himmelfahrt, Pfingsten, Fronleichnam und Ostern.";
      canonical = "https://smart-rechnr.de/brueckentage-planer";
    } else if (currentPath === '/waermepumpe' || currentPath === '/waermepumpen-rechner' || currentPath === '/waermepumpe-rechner') {
      title = "Wärmepumpenrechner 2027 – Kosten, 70% Förderung & Amortisation";
      description = "Lohnt sich eine Wärmepumpe 2027? Berechnen Sie laufende Heizkosten, staatliche KfW-Förderung (bis zu 70 %) und die Amortisationszeit gegenüber einer Gasheizung.";
      canonical = "https://smart-rechnr.de/waermepumpe";
    } else if (currentPath === '/gasverbrauch' || currentPath === '/gasverbrauchsrechner' || currentPath === '/gasverbrauch-rechner') {
      title = "Gasverbrauch berechnen 2027 – m³ in kWh & Gaskosten Rechner";
      description = "Gasverbrauch 2027 exakt umrechnen: Kubikmeter (m³) in Kilowattstunden (kWh) nach DVGW G 685. Inklusive Brennwert, Zustandszahl, ETS-2 CO2-Preis & Kostenrechner.";
      canonical = "https://smart-rechnr.de/gasverbrauch";
    } else if (currentPath === '/stromverbrauch' || currentPath === '/stromverbrauchsrechner' || currentPath === '/stromverbrauch-rechner') {
      title = "Stromverbrauch berechnen 2027 – Haushalts-Vergleich & Stromspiegel";
      description = "Stromverbrauch 2027 für Haushalte berechnen: Ermitteln Sie den Jahresverbrauch nach Personen, Wohnfläche & Warmwasser. Stromspiegel-Vergleich & Einsparpotenziale.";
      canonical = "https://smart-rechnr.de/stromverbrauch";
    } else if (currentPath === '/stromkosten' || currentPath === '/stromkostenrechner' || currentPath === '/stromkosten-rechner') {
      title = "Stromkostenrechner 2027 – Gerätekosten pro Tag, Monat & Jahr berechnen";
      description = "Stromkostenrechner für Haushaltsgeräte 2027: Berechnen Sie den Strompreis pro Nutzung, Tag, Monat & Jahr für Waschmaschine, TV, Kühlschrank & PC. Inklusive Spartipps.";
      canonical = "https://smart-rechnr.de/stromkosten";
    } else if (currentPath === '/led-watt' || currentPath === '/led-watt-rechner' || currentPath === '/led-watt-umrechner') {
      title = "LED-Watt-Rechner 2027 – Lumen in Watt umrechnen & Stromkosten";
      description = "Lumen in Watt umrechnen 2027: Vergleichen Sie LEDs mit Glühbirnen, Halogen & Leuchtstoffröhren. Inklusive Raumgrößen-Planer, Lichtausbeute (lm/W) & Stromersparnis.";
      canonical = "https://smart-rechnr.de/led-watt";
    } else if (currentPath === '/volt-watt-amp' || currentPath === '/volt-watt-ampere-rechner' || currentPath === '/volt-watt-ampere-converter' || currentPath === '/volt-watt') {
      title = "Volt-Watt-Ampere-Rechner 2027 – Spannung, Strom & Leistung umrechnen";
      description = "Volt-Watt-Ampere-Rechner: Berechnen Sie Leistung (Watt), Stromstärke (Ampere), Spannung (Volt) und Widerstand (Ohm) online nach den Formeln P = U × I und U = R × I.";
      canonical = "https://smart-rechnr.de/volt-watt-amp";
    } else if (
      currentPath === '/waehrungsrechner' ||
      currentPath === '/waehrungsumrechner' ||
      currentPath === '/geldanlage-sparen/waehrungsrechner' ||
      currentPath === '/waehrungcategory/waehrungsrechner' ||
      currentPath === '/waehrungs-rechner'
    ) {
      title = "Währungsrechner 2027 – Euro, Dollar, Pfund & Zloty umrechnen";
      description = "Offizieller Währungsrechner: Euro in Dollar, Pfund & Zloty mit tagesaktuellen EZB-Referenzkursen umrechnen. Inklusive Formel, Gebühren-Vergleich & Matrix.";
      canonical = "https://smart-rechnr.de/waehrungsrechner";
    } else if (
      currentPath === '/rechner-einbinden' ||
      currentPath === '/widget-generator' ||
      currentPath === '/rechner-widget' ||
      currentPath === '/widget' ||
      currentPath === '/embed' ||
      currentPath === '/rechner-einbetten'
    ) {
      title = "Online-Rechner kostenlos einbinden – Widget- & iFrame-Generator | smart-rechnr.de";
      description = "Online-Rechner kostenlos einbinden: Responsiven iFrame- & WordPress-Code für Brutto-Netto, Steuern & Finanzen generieren. 100% werbefrei & DSGVO-konform.";
      canonical = "https://smart-rechnr.de/rechner-einbinden";
    } else if (
      currentPath === '/bankverbindung-pruefen' ||
      currentPath === '/bank-pruefen'
    ) {
      title = "Bankverbindung prüfen – IBAN, BLZ & BIC online validieren";
      description = "Bankverbindung kostenlos prüfen: IBAN nach MOD 97-10 validieren, Bankname, BLZ & BIC ermitteln. Offizielle Bundesbank-Daten 2027. Schnell & 100% datensicher!";
      canonical = "https://smart-rechnr.de/bankverbindung-pruefen";
    } else if (
      currentPath === '/blz-suche' ||
      currentPath === '/bankleitzahlensuche' ||
      currentPath === '/bankleitzahlen-suche'
    ) {
      title = "Bankleitzahl suchen – BLZ, BIC und Bank finden";
      description = "Bankleitzahlensuche: Finden Sie BLZ, BIC, Hauptsitz und Filialen aller Banken & Sparkassen in Deutschland. Offizielles Bundesbank-Verzeichnis 2027. Gratis!";
      canonical = "https://smart-rechnr.de/blz-suche";
    } else if (
      currentPath === '/etf-sparplanrechner' ||
      currentPath === '/etf-sparplan'
    ) {
      title = "ETF-Sparplan Rechner 2027 – Zinseszins & Vorabpauschale";
      description = "ETF-Sparplan 2027 berechnen: Exakter Zinseszins, Vorabpauschale nach § 18 InvStG, Sparerpauschbetrag (1.000 €) & 30 % Teilfreistellung. Jetzt online simulieren!";
      canonical = "https://smart-rechnr.de/etf-sparplanrechner";
    } else if (
      currentPath === '/entnahmeplanrechner' ||
      currentPath === '/entnahmeplan' ||
      currentPath === '/etf-entnahmeplan'
    ) {
      title = "Entnahmeplanrechner 2027 – ETF Entnahmeplan & Kapitalverzehr berechnen";
      description = "Kostenloser Entnahmeplanrechner 2027: Berechnen Sie monatliche Entnahmerate, Restkapital und Laufzeit für ETFs, Festgeld und Aktien. Mit Kapitalverzehr, ewiger Rente, Steuer & Inflation.";
      canonical = "https://smart-rechnr.de/entnahmeplanrechner";
    } else if (
      currentPath === '/bankrecherche' ||
      currentPath === '/banksuche' ||
      currentPath === '/bank-suche'
    ) {
      title = "Bankrecherche & Bankensuche 2027 – Name, PLZ, Ort, BLZ & BIC finden";
      description = "Offizielle Bankrecherche 2027: Bankensuche nach Name, PLZ, Ort, BLZ oder BIC. Ermitteln Sie Banknamen, Hauptsitze und SWIFT-Codes oder nutzen Sie die umgekehrte BIC Suche mit IBAN.";
      canonical = "https://smart-rechnr.de/bankrecherche";
    } else if (
      currentPath === '/iban-rechner' ||
      currentPath === '/iban-berechnen' ||
      currentPath === '/iban-generator'
    ) {
      title = "IBAN Rechner 2027 – IBAN & BIC berechnen, Sparkasse, Volksbank";
      description = "Kostenloser IBAN Rechner 2027: Berechnen Sie die 22-stellige deutsche IBAN und den passenden BIC aus BLZ & Kontonummer. Inklusive Modulo 97-10 Prüfziffer und IBAN-Validierung.";
      canonical = "https://smart-rechnr.de/iban-rechner";
    } else if (
      currentPath === '/bmi-rechner' ||
      currentPath === '/gesundheit/bmi-rechner' ||
      currentPath === '/bmi'
    ) {
      title = "BMI-Rechner 2027 – Body Mass Index, WHtR & Kalorienbedarf berechnen";
      description = "Kostenloser BMI-Rechner 2027: Body-Mass-Index exakt berechnen für Frauen & Männer. Mit WHO-Klassifikation, NRC-Alterskorrektur, WHtR-Bauchfett-Analyse & BMR-Kalorien.";
      canonical = "https://smart-rechnr.de/bmi-rechner";
    } else if (
      currentPath === '/bmi-rechner-kinder' ||
      currentPath === '/bmi-kinder' ||
      currentPath === '/gesundheit/bmi-rechner-kinder' ||
      currentPath === '/gesundheit/bmi-kinder'
    ) {
      title = "BMI Rechner Kinder 2027 – Perzentilen nach KiGGS & WHO für Jungen & Mädchen";
      description = "Offizieller BMI Rechner für Kinder und Jugendliche (1–18 Jahre): Berechnung nach Kromeyer-Hauschild & RKI KiGGS Perzentilenkurven (P3–P97). Pädiatrisch geprüft & anonym.";
      canonical = "https://smart-rechnr.de/bmi-rechner-kinder";
    }

    // Update document title
    document.title = title;

    // Update meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", description);
    } else {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      metaDesc.setAttribute('content', description);
      document.head.appendChild(metaDesc);
    }

    // Update canonical URL
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (linkCanonical) {
      linkCanonical.setAttribute("href", canonical);
    } else {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      linkCanonical.setAttribute('href', canonical);
      document.head.appendChild(linkCanonical);
    }

    // Update OpenGraph / Social tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", title);

    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", description);

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", canonical);

    // Update Twitter Card tags dynamically
    const twTitle = document.querySelector('meta[property="twitter:title"]') || document.querySelector('meta[name="twitter:title"]');
    if (twTitle) twTitle.setAttribute("content", title);

    const twDesc = document.querySelector('meta[property="twitter:description"]') || document.querySelector('meta[name="twitter:description"]');
    if (twDesc) twDesc.setAttribute("content", description);

    const twUrl = document.querySelector('meta[property="twitter:url"]') || document.querySelector('meta[name="twitter:url"]');
    if (twUrl) twUrl.setAttribute("content", canonical);

    // Select dynamic category-tailored high-converting social preview thumbnail
    let ogImage = 'https://smart-rechnr.de/og-image.jpg?v=2';
    let ogAlt = 'Smart Rechner 2026 – Online-Rechner für Steuern, Gehalt & Finanzen';

    if (
      currentPath.includes('brutto') || 
      currentPath.includes('steuer') || 
      currentPath.includes('gehalt') || 
      currentPath.includes('lohn') || 
      currentPath.includes('nettopreis') || 
      currentPath.includes('abgeltung') || 
      currentPath.includes('aussergewoehnliche') || 
      currentPath.includes('erbschaft')
    ) {
      ogImage = 'https://smart-rechnr.de/og-brutto-netto.jpg?v=2';
      ogAlt = 'Smart Rechner 2026 – Gehalt, Brutto-Netto & Steuern exakt berechnen';
    } else if (currentPath.includes('arbeitslosengeld')) {
      ogImage = 'https://smart-rechnr.de/og-arbeitslosengeld.jpg';
      ogAlt = 'Arbeitslosengeld Rechner 2026 / 2027 – ALG 1 Anspruch, Höhe, Bezugsdauer & Sperrzeit';
    } else if (currentPath.includes('abfindung')) {
      ogImage = 'https://smart-rechnr.de/og-abfindungsrechner.jpg';
      ogAlt = 'Abfindungsrechner 2026 / 2027 – Fünftelregelung nach § 34 EStG & Netto berechnen';
    } else if (
      currentPath.includes('buergergeld') || 
      currentPath.includes('wohngeld') || 
      currentPath.includes('bafoeg') || 
      currentPath.includes('elterngeld') || 
      currentPath.includes('elternzeit')
    ) {
      ogImage = 'https://smart-rechnr.de/og-soziales.jpg?v=2';
      ogAlt = 'Smart Rechner 2026 – Bürgergeld, Wohngeld & Sozialleistungen berechnen';
    } else if (
      currentPath.includes('et-rechner') || 
      currentPath.includes('ssw') || 
      currentPath.includes('eisprung') || 
      currentPath.includes('schwangerschaft') ||
      currentPath.includes('geburt')
    ) {
      ogImage = 'https://smart-rechnr.de/og-familie.jpg?v=2';
      ogAlt = 'Smart Rechner 2026 – Geburtstermin, SSW & Schwangerschaftsrechner';
    } else if (currentPath.includes('gasverbrauch')) {
      ogImage = 'https://smart-rechnr.de/images/gasverbrauch-rechner-2027.jpg';
      ogAlt = 'Gasverbrauch berechnen 2027 – m³ in kWh & Gaskosten Rechner';
    } else if (currentPath.includes('led-watt')) {
      ogImage = 'https://smart-rechnr.de/images/led-watt-rechner-2027.jpg';
      ogAlt = 'LED-Watt-Rechner 2027 – Lumen in Watt umrechnen & Stromkosten';
    } else if (currentPath.includes('stromkosten')) {
      ogImage = 'https://smart-rechnr.de/images/stromkosten-rechner-2027.jpg';
      ogAlt = 'Stromkostenrechner 2027 – Gerätekosten pro Tag, Monat & Jahr';
    } else if (currentPath.includes('stromverbrauch')) {
      ogImage = 'https://smart-rechnr.de/images/stromverbrauch-rechner-2027.jpg';
      ogAlt = 'Stromverbrauch berechnen 2027 – Haushalts-Vergleich & Stromspiegel';
    } else if (currentPath.includes('volt-watt') || currentPath.includes('volt-watt-amp')) {
      ogImage = 'https://smart-rechnr.de/images/volt-watt-ampere-rechner-2027.jpg';
      ogAlt = 'Volt-Watt-Ampere-Rechner 2027 – Spannung, Strom & Leistung';
    } else if (currentPath.includes('waermepumpe')) {
      ogImage = 'https://smart-rechnr.de/images/waermepumpen-rechner-2027.jpg';
      ogAlt = 'Wärmepumpenrechner 2027 – Kosten, 70% Förderung & Amortisation';
    } else if (currentPath.includes('waehrungsrechner') || currentPath.includes('waehrung')) {
      ogImage = 'https://smart-rechnr.de/images/waehrungsrechner-2027.jpg';
      ogAlt = 'Währungsrechner 2027 – Euro, Dollar, Pfund & Zloty live umrechnen';
    } else if (currentPath.includes('bank-pruefen') || currentPath.includes('bankverbindung')) {
      ogImage = 'https://smart-rechnr.de/og-bankverbindung.jpg';
      ogAlt = 'Bankverbindung prüfen – IBAN, BLZ & BIC online validieren';
    } else if (currentPath.includes('blz') || currentPath.includes('bankleitzahl')) {
      ogImage = 'https://smart-rechnr.de/og-blz-suche.jpg';
      ogAlt = 'Bankleitzahl suchen – BLZ, BIC und Bank finden';
    } else if (currentPath.includes('etf-sparplan') || currentPath.includes('etf-sparplanrechner')) {
      ogImage = 'https://smart-rechnr.de/images/etf-sparplanrechner-2027.webp';
      ogAlt = 'ETF-Sparplan Rechner 2027 – Zinseszins & Vorabpauschale';
    } else if (currentPath.includes('entnahmeplan')) {
      ogImage = 'https://smart-rechnr.de/images/entnahmeplanrechner-2027.webp';
      ogAlt = 'Entnahmeplanrechner 2027 – ETF Entnahmeplan & Kapitalverzehr berechnen';
    } else if (currentPath.includes('bankrecherche') || currentPath.includes('banksuche')) {
      ogImage = 'https://smart-rechnr.de/images/bankrecherche-2027.webp';
      ogAlt = 'Bankrecherche & Bankensuche 2027 – Name, PLZ, BLZ und BIC';
    } else if (currentPath.includes('iban-rechner') || currentPath.includes('iban')) {
      ogImage = 'https://smart-rechnr.de/images/iban-rechner-2027.webp';
      ogAlt = 'IBAN Rechner 2027 – IBAN & BIC berechnen, Sparkasse, Volksbank';
    } else if (currentPath.includes('bmi-rechner-kinder') || currentPath.includes('bmi-kinder')) {
      ogImage = 'https://smart-rechnr.de/images/bmi-rechner-kinder-2027.webp';
      ogAlt = 'BMI Rechner Kinder 2027 – Perzentilen nach KiGGS & WHO für Jungen & Mädchen';
    } else if (currentPath.includes('bmi-rechner') || currentPath.includes('bmi')) {
      ogImage = 'https://smart-rechnr.de/images/bmi-rechner-2027.webp';
      ogAlt = 'BMI-Rechner 2027 – Body Mass Index, WHtR & Kalorienbedarf berechnen';
    } else if (currentPath.includes('eisprung')) {
      ogImage = 'https://smart-rechnr.de/images/eisprungrechner-2027.webp';
      ogAlt = 'Eisprungrechner 2027 – Fruchtbare Tage, Ovulation & Einnistung berechnen';
    }

    const updateOrCreateMeta = (selector: string, attrName: string, attrVal: string, content: string) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    updateOrCreateMeta('meta[property="fb:app_id"]', 'property', 'fb:app_id', '966242223397117');
    updateOrCreateMeta('meta[property="fb:pages"]', 'property', 'fb:pages', 'smartrechnr');

    updateOrCreateMeta('meta[property="og:image"]', 'property', 'og:image', ogImage);
    updateOrCreateMeta('meta[property="og:image:secure_url"]', 'property', 'og:image:secure_url', ogImage);
    updateOrCreateMeta('meta[property="og:image:type"]', 'property', 'og:image:type', 'image/jpeg');
    updateOrCreateMeta('meta[property="og:image:width"]', 'property', 'og:image:width', '1200');
    updateOrCreateMeta('meta[property="og:image:height"]', 'property', 'og:image:height', '630');
    updateOrCreateMeta('meta[property="og:image:alt"]', 'property', 'og:image:alt', ogAlt);

    updateOrCreateMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    updateOrCreateMeta('meta[name="twitter:image"]', 'name', 'twitter:image', ogImage);
    updateOrCreateMeta('meta[name="twitter:image:alt"]', 'name', 'twitter:image:alt', ogAlt);
  }, [currentPath]);

  // Smooth-scroll to the requested calculator group when a path route is activated
  useEffect(() => {
    if (!currentPath || currentPath === '/') return;
    
    const matchedCategory = CATEGORIES_DATA.find(cat => 
      cat.calculators.some(calc => {
        const cleanCalcPath = calc.path.startsWith('#') ? calc.path.substring(1) : calc.path;
        return cleanCalcPath === currentPath;
      }) || `/${cat.id}` === currentPath
    );
    
    if (matchedCategory) {
      setTimeout(() => {
        const el = document.getElementById(`group-card-${matchedCategory.id}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Temporarily highlight the category card to emphasize
          el.classList.add('ring-4', 'ring-blue-600/30', 'border-blue-500');
          setTimeout(() => {
            el.classList.remove('ring-4', 'ring-blue-600/30', 'border-blue-500');
          }, 2500);
        }
      }, 150);
    }
  }, [currentPath]);

  // Unified list of all 200+ calculators for instant search query tracking
  const allCalculatorsList = useMemo(() => {
    const list: (CalculatorItem & { categoryTitle: string })[] = [];
    CATEGORIES_DATA.forEach(cat => {
      cat.calculators.forEach(calc => {
        list.push({ ...calc, categoryTitle: cat.title });
      });
    });
    return list;
  }, []);

  // Filtered and ranked list based on Advanced Fuzzy Search algorithm & scoring
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    
    const results: {
      calc: CalculatorItem & { categoryTitle: string };
      score: number;
      boldIndices: Set<number>;
    }[] = [];

    const q = searchQuery.toLowerCase().trim();

    allCalculatorsList.forEach(calc => {
      const targetName = calc.name;
      const t = targetName.toLowerCase();
      const c = calc.categoryTitle.toLowerCase();
      const e = calc.englishName.toLowerCase();

      let score = 0;
      const boldIndices = new Set<number>();

      // 1. Exact match gets giant points
      if (t === q) {
        score += 2000;
      }
      // 2. Starts with gets high points
      else if (t.startsWith(q)) {
        score += 1200;
        for (let i = 0; i < q.length; i++) {
          boldIndices.add(i);
        }
      }
      // 3. Contiguous substring gets solid points
      else if (t.includes(q)) {
        score += 800;
        const startIdx = t.indexOf(q);
        for (let i = 0; i < q.length; i++) {
          boldIndices.add(startIdx + i);
        }
      }

      // 4. English name boost
      if (e === q) {
        score += 400;
      } else if (e.includes(q)) {
        score += 200;
      }

      // 5. Category name boost
      if (c.includes(q)) {
        score += 150;
      }

      // Alphabet/alias helper translation extensions
      if (q === "est" && t.includes("einkommensteuer")) score += 1000;
      if ((q === "soli" || q === "sol") && t.includes("solidarit")) score += 1000;
      if (q === "steuer" && (t.includes("steuer") || t.includes("lohnsteuer"))) score += 500;
      if (q === "netto" && t.includes("brutto-netto")) score += 1000;
      if (q === "brutto" && t.includes("brutto-netto")) score += 1000;
      if ((q.includes("abgeltung") || q.includes("kapitalertrag") || q.includes("aktien") || q.includes("fonds") || q.includes("etf") || q.includes("zinsen") || q.includes("dividende")) && (t.includes("abgeltung") || t.includes("kapitalertrag"))) score += 1500;
      if ((q.includes("aussergew") || q.includes("belastung") || q.includes("krankheit") || q.includes("pflege") || q.includes("bestattung") || q.includes("zumutbar")) && t.includes("belastung")) score += 1500;
      if ((q.includes("arbeitstag") || q.includes("werktag") || q.includes("pendler")) && t.includes("arbeitstage")) score += 1500;
      if ((q.includes("datum") || q.includes("tage") || q.includes("zeitspanne") || q.includes("frist")) && t.includes("datumsrechner")) score += 1500;
      if ((q.includes("geburt") || q.includes("entbindung") || q.includes("naegele") || q === "et" || q.includes("et rechner") || q.includes("geburtstermin")) && (t.includes("et-rechner") || t.includes("geburtstermin"))) score += 1600;
      if ((q.includes("ssw") || q.includes("schwangerschaftswoche") || q.includes("trimester") || q.includes("mutterpass")) && t.includes("ssw-rechner")) score += 1600;
      if ((q.includes("felge") || q.includes("einpresstiefe") || q.includes("spurplatte") || q.includes("distanzscheibe") || (q.includes("et") && q.includes("felg"))) && t.includes("et-rechner-felgen")) score += 1600;
      if ((q.includes("restwert") || q.includes("wertverlust") || q.includes("autowert") || q.includes("fahrzeugwert") || q.includes("schwacke") || q.includes("gebrauchtwagen")) && (t.includes("restwert") || t.includes("autowert") || t.includes("wertverlust"))) score += 1800;
      if ((q.includes("eisprung") || q.includes("ovulation") || q.includes("fruchtbar") || q.includes("luteal") || q.includes("nidation") || q.includes("nmt")) && t.includes("eisprungrechner")) score += 1600;
      if ((q.includes("feiertag") || q.includes("brückentag") || q.includes("brueckentag") || q.includes("nrw") || q.includes("bayern") || q.includes("himmelfahrt") || q.includes("pfingst") || q.includes("ostern")) && (t.includes("feiertag") || t.includes("brückentag"))) score += 1800;

      // 6. Subsequence mapping inside word (e.g. searching "bto" in "Brutto")
      let qIdx = 0;
      const matchedPositions: number[] = [];
      for (let i = 0; i < t.length && qIdx < q.length; i++) {
        if (t[i] === q[qIdx]) {
          matchedPositions.push(i);
          qIdx++;
        }
      }
      if (qIdx === q.length) {
        const span = matchedPositions[matchedPositions.length - 1] - matchedPositions[0] + 1;
        const tightness = Math.max(1, 100 - (span - q.length) * 8);
        score += 200 + tightness;
        matchedPositions.forEach(idx => boldIndices.add(idx));
      }

      // 7. Tokenized matching (e.g. "brutto netto" matches both tokens)
      const queryWords = q.split(/[\s\-]+/);
      if (queryWords.length > 1) {
        let tokenMatches = 0;
        queryWords.forEach(word => {
          if (word) {
            if (t.includes(word)) {
              tokenMatches++;
              score += 150;
              let start = 0;
              while (true) {
                const idx = t.indexOf(word, start);
                if (idx === -1) break;
                for (let i = 0; i < word.length; i++) {
                  boldIndices.add(idx + i);
                }
                start = idx + word.length;
              }
            } else {
              // Subsequence match on token
              let subQIdx = 0;
              const subMatched: number[] = [];
              for (let i = 0; i < t.length && subQIdx < word.length; i++) {
                if (t[i] === word[subQIdx]) {
                  subMatched.push(i);
                  subQIdx++;
                }
              }
              if (subQIdx === word.length) {
                tokenMatches++;
                score += 50;
                subMatched.forEach(idx => boldIndices.add(idx));
              }
            }
          }
        });
        if (tokenMatches === queryWords.length) {
          score += 400; // Complete multi-word match
        }
      }

      // Prioritize active implemented calculators
      if (calc.isImplemented) {
        score += 300;
      }

      // If we got positive points, append to candidates
      if (score > 100) {
        results.push({ calc, score, boldIndices });
      }
    });

    // Rank descending by match score
    return results.sort((a, b) => b.score - a.score).slice(0, 10);
  }, [searchQuery, allCalculatorsList]);

  // Navigate directly using clean URL pathnames
  const navigateTo = (path: string) => {
    const target = path.startsWith('#') ? path.substring(1) : path;
    const clean = target.startsWith('/') ? target : `/${target}`;
    window.history.pushState(null, '', clean);
    window.dispatchEvent(new Event('popstate'));
    setSearchQuery('');
    setIsSearchOpen(false);
  };

  const triggerHashUpdate = (id: string) => {
    navigateTo(id);
  };

  // Carousel Helper handlers
  const handleNextSlide = (catId: string, limit: number, total: number) => {
    setCategorySliders(prev => {
      const current = prev[catId] || 0;
      const next = current + limit >= total ? 0 : current + limit;
      return { ...prev, [catId]: next };
    });
  };

  const handlePrevSlide = (catId: string, limit: number, total: number) => {
    setCategorySliders(prev => {
      const current = prev[catId] || 0;
      const next = current - limit < 0 ? Math.max(0, total - limit) : current - limit;
      return { ...prev, [catId]: next };
    });
  };

  const isLegalRoute = currentPath === '/impressum' || currentPath === '/datenschutz' || currentPath === '/haftungsausschluss' || currentPath === '/kontakt';
  const isCalculatorRoute = [
    '/abfindungsrechner',
    '/arbeit-soziales/abfindungsrechner',
    '/brutto-netto',
    '/steuerrechner/brutto-netto',
    '/bruttorechner',
    '/brutto-rechner',
    '/steuerrechner/bruttorechner',
    '/steuerrechner/brutto-rechner',
    '/einkommensteuer',
    '/steuerrechner/einkommensteuer',
    '/lohnsteuerrechner',
    '/steuerrechner/lohnsteuerrechner',
    '/nettopreis-rechner',
    '/steuerrechner/nettopreis-rechner',
    '/abgeltungsteuerrechner',
    '/abgeltungssteuer-rechner',
    '/steuerrechner/abgeltungsteuerrechner',
    '/steuerrechner/abgeltungssteuer-rechner',
    '/kapitalertragsteuer-rechner',
    '/steuerrechner/kapitalertragsteuer-rechner',
    '/aussergewoehnliche-belastungen',
    '/steuerrechner/aussergewoehnliche-belastungen',
    '/aussergewoehnliche-belastungen-rechner',
    '/steuerrechner/aussergewoehnliche-belastungen-rechner',
    '/steuer-berechnen',
    '/steuerrechner/steuer-berechnen',
    '/erbschaftsteuer-rechner',
    '/erbschaftsteuerrechner',
    '/steuerrechner/erbschaftsteuer-rechner',
    '/erbfolge-rechner',
    '/erbfolgerechner',
    '/recht/erbfolge-rechner',
    '/steuerrechner/erbfolge-rechner',
    '/familie/erbfolge-rechner',
    '/arbeitslosengeld-rechner',
    '/arbeit-soziales/arbeitslosengeld-rechner',
    '/bafoeg-rechner',
    '/arbeit-soziales/bafoeg-rechner',
    '/wohngeldrechner',
    '/arbeit-soziales/wohngeldrechner',
    '/buergergeld-rechner',
    '/arbeit-soziales/buergergeld-rechner',
    '/elterngeld-rechner',
    '/arbeit-soziales/elterngeld-rechner',
    '/elternzeitrechner',
    '/arbeit-soziales/elternzeitrechner',
    '/laengenumrechner',
    '/umrechner/laengenumrechner',
    '/arbeitszeitrechner',
    '/arbeit-soziales/arbeitszeitrechner',
    '/zeit-datum/arbeitszeitrechner',
    '/altersrechner',
    '/zeit-datum/altersrechner',
    '/alters-rechner',
    '/arbeitstage-rechner',
    '/arbeitstagerechner',
    '/zeit-datum/arbeitstage-rechner',
    '/zeit-datum/arbeitstagerechner',
    '/zeitrechner',
    '/zeit-datum/zeitrechner',
    '/datumsrechner',
    '/zeit-datum/datumsrechner',
    '/et-rechner',
    '/gesundheit/et-rechner',
    '/familie/et-rechner',
    '/geburtstermin',
    '/geburtsterminrechner',
    '/ssw-rechner',
    '/gesundheit/ssw-rechner',
    '/familie/ssw-rechner',
    '/et-rechner-ssw',
    '/schwangerschaftswochen-rechner',
    '/et-rechner-felgen',
    '/umrechner/et-rechner-felgen',
    '/kfz/et-rechner-felgen',
    '/felgenrechner',
    '/einpresstiefe-rechner',
    '/restwert-rechner',
    '/kredit-finanzierung/restwert-rechner',
    '/kfz/restwert-rechner',
    '/auto-wertverlust-rechner',
    '/fahrzeugwert-rechner',
    '/autowert-rechner',
    '/eisprungrechner',
    '/familie/eisprungrechner',
    '/et-rechner-eisprung',
    '/ovulationsrechner',
    '/feiertage-2026',
    '/feiertage-in-deutschland',
    '/feiertage-2027',
    '/feiertage',
    '/feiertage-2025',
    '/feiertage-nrw-2026',
    '/feiertage-2026-nrw',
    '/nrw-feiertage-2026',
    '/feiertage-nrw-2025',
    '/feiertage-bayern-2026',
    '/feiertage-bayern-2025',
    '/feiertage-bw-2026',
    '/feiertage-baden-wuerttemberg-2026',
    '/feiertage-hessen-2026',
    '/feiertage-niedersachsen-2026',
    '/feiertage-berlin-2026',
    '/feiertage-sachsen-2026',
    '/feiertage-sachsen-2025',
    '/feiertage-rlp-2026',
    '/feiertage-hamburg-2026',
    '/feiertage-mai-2026',
    '/brueckentage-planer',
    '/waermepumpe',
    '/waermepumpen-rechner',
    '/energie/waermepumpe',
    '/gasverbrauch',
    '/gasverbrauchsrechner',
    '/energie/gasverbrauch',
    '/stromverbrauch',
    '/stromverbrauchsrechner',
    '/energie/stromverbrauch',
    '/stromkosten',
    '/stromkostenrechner',
    '/energie/stromkosten',
    '/led-watt',
    '/led-watt-rechner',
    '/energie/led-watt',
    '/volt-watt-amp',
    '/volt-watt-ampere-rechner',
    '/energie/volt-watt-amp',
    '/waehrungsrechner',
    '/waehrungsumrechner',
    '/waehrungs-rechner',
    '/geldanlage-sparen/waehrungsrechner',
    '/waehrungcategory/waehrungsrechner',
    '/rechner-einbinden',
    '/widget-generator',
    '/rechner-widget',
    '/widget',
    '/embed',
    '/rechner-einbetten',
    '/bankverbindung-pruefen',
    '/bank-pruefen',
    '/blz-suche',
    '/bankleitzahlensuche',
    '/etf-sparplanrechner',
    '/etf-sparplan',
    '/entnahmeplanrechner',
    '/entnahmeplan',
    '/etf-entnahmeplan',
    '/bankrecherche',
    '/banksuche',
    '/iban-rechner',
    '/iban-berechnen',
    '/bmi-rechner',
    '/gesundheit/bmi-rechner',
    '/bmi-rechner-kinder',
    '/bmi-kinder',
    '/gesundheit/bmi-rechner-kinder',
    '/gesundheit/bmi-kinder',
    '/eisprungrechner',
    '/gesundheit/eisprungrechner'
  ].includes(currentPath);
  const isHomeRoute = [
    '/',
    '',
    '/arbeit-soziales',
    '/steuerrechner',
    '/geldanlage-sparen',
    '/kredit-finanzierung',
    '/zeit-datum',
    '/gesundheit',
    '/gesundheit-sport',
    '/mathematik-sonstiges'
  ].includes(currentPath);

  // Check if loaded in embed mode (via query string ?embed=true or hash)
  const isEmbedMode = typeof window !== 'undefined' && (
    new URLSearchParams(window.location.search).get('embed') === 'true' ||
    window.location.hash.includes('embed=true')
  );

  // If in embed mode, render clean widget without header/footer + attribution badge
  if (isEmbedMode) {
    return (
      <div className="min-h-screen bg-transparent p-2 sm:p-4 text-[#334155] font-sans is-embed-mode" id="embed-widget-container">
        <Suspense fallback={
          <div className="flex flex-col items-center justify-center py-20 space-y-3">
            <div className="animate-spin rounded-full h-8 w-8 border-3 border-slate-200 border-t-[#004a99]"></div>
            <span className="text-xs font-semibold text-slate-400">Rechner wird geladen...</span>
          </div>
        }>
          {/* Calculators render inside embed */}
          {(currentPath === '/abfindungsrechner' || currentPath === '/arbeit-soziales/abfindungsrechner') && (
            <SeveranceCalculator onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/brutto-netto' || currentPath === '/steuerrechner/brutto-netto') && (
            <BruttoNettoCalculator onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/bruttorechner' || currentPath === '/brutto-rechner' || currentPath === '/steuerrechner/bruttorechner') && (
            <BruttoRechnerCalculator onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/einkommensteuer' || currentPath === '/steuerrechner/einkommensteuer') && (
            <EinkommensteuerCalculator onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/lohnsteuerrechner' || currentPath === '/steuerrechner/lohnsteuerrechner') && (
            <LohnsteuerCalculator onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/nettopreis-rechner' || currentPath === '/steuerrechner/nettopreis-rechner') && (
            <NettopreisCalculator onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/abgeltungsteuerrechner' || currentPath === '/abgeltungssteuer-rechner') && (
            <AbgeltungssteuerCalculator onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/aussergewoehnliche-belastungen' || currentPath === '/aussergewoehnliche-belastungen-rechner') && (
            <AussergewoehnlicheBelastungenCalculator onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/steuer-berechnen' || currentPath === '/steuerrechner/steuer-berechnen') && (
            <SteuerrechnerCalculator onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/erbschaftsteuer-rechner' || currentPath === '/erbschaftsteuerrechner') && (
            <ErbschaftsteuerCalculator onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/erbfolge-rechner' || currentPath === '/erbfolgerechner' || currentPath === '/recht/erbfolge-rechner' || currentPath === '/steuerrechner/erbfolge-rechner' || currentPath === '/familie/erbfolge-rechner') && (
            <ErbfolgeCalculator onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/arbeitslosengeld-rechner') && (
            <ArbeitslosengeldCalculator onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/bafoeg-rechner') && (
            <BafoegCalculator onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/wohngeldrechner') && (
            <WohngeldCalculator onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/buergergeld-rechner') && (
            <BuergergeldCalculator onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/elterngeld-rechner') && (
            <ElterngeldCalculator onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/elternzeitrechner') && (
            <Elternzeitrechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/arbeitszeitrechner') && (
            <Arbeitszeitrechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/altersrechner') && (
            <AltersrechnerCalculator onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/arbeitstage-rechner') && (
            <ArbeitstageRechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/zeitrechner') && (
            <ZeitrechnerCalculator onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/datumsrechner') && (
            <DatumsrechnerCalculator onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/et-rechner') && (
            <EtRechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/ssw-rechner') && (
            <SswRechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/et-rechner-felgen') && (
            <EtFelgenRechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/restwert-rechner') && (
            <RestwertRechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/eisprungrechner') && (
            <EisprungRechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/laengenumrechner') && (
            <Laengenumrechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/waermepumpe' || currentPath === '/waermepumpen-rechner') && (
            <WaermepumpenRechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/gasverbrauch' || currentPath === '/gasverbrauchsrechner') && (
            <Gasverbrauchsrechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/stromverbrauch' || currentPath === '/stromverbrauchsrechner') && (
            <Stromverbrauchsrechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/stromkosten' || currentPath === '/stromkostenrechner') && (
            <Stromkostenrechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/led-watt' || currentPath === '/led-watt-rechner') && (
            <LedWattRechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/volt-watt-amp' || currentPath === '/volt-watt-ampere-rechner') && (
            <VoltWattAmpereRechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/waehrungsrechner' || currentPath === '/waehrungsumrechner') && (
            <Waehrungsrechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/bankverbindung-pruefen' || currentPath === '/bank-pruefen') && (
            <BankverbindungPruefen onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/blz-suche' || currentPath === '/bankleitzahlensuche') && (
            <Bankleitzahlensuche onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/etf-sparplanrechner' || currentPath === '/etf-sparplan') && (
            <EtfSparplanrechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/entnahmeplanrechner' || currentPath === '/entnahmeplan') && (
            <Entnahmeplanrechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/bankrecherche' || currentPath === '/banksuche') && (
            <Bankrecherche onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/iban-rechner' || currentPath === '/iban-berechnen') && (
            <IbanRechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/bmi-rechner' || currentPath === '/gesundheit/bmi-rechner') && (
            <BmiRechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/bmi-rechner-kinder' || currentPath === '/bmi-kinder' || currentPath === '/gesundheit/bmi-rechner-kinder' || currentPath === '/gesundheit/bmi-kinder') && (
            <BmiKinderRechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
          {(currentPath === '/eisprungrechner' || currentPath === '/gesundheit/eisprungrechner' || currentPath === '/familie/eisprungrechner') && (
            <EisprungRechner onBackToPortal={() => {}} navigateTo={navigateTo} />
          )}
        </Suspense>

        {/* Embedded Attribution Backlink footer */}
        <div className="mt-3 pt-2 border-t border-slate-200/80 flex items-center justify-between text-[11px] text-slate-500 font-sans">
          <span className="font-medium">100% Anonym &amp; DSGVO-konform</span>
          <span>
            Rechner von{' '}
            <a
              href={`https://smart-rechnr.de${currentPath}`}
              target="_blank"
              rel="noopener"
              className="font-bold text-[#004a99] hover:underline"
            >
              smart-rechnr.de
            </a>
          </span>
        </div>
      </div>
    );
  }

  // ELSE - Show the full smart-rechnr.de official portal homepage directory!
  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc] text-[#334155] overflow-x-hidden" id="app-root">
      
      {/* Brand Header */}
      <Navbar currentTab={'' as CalculatorType} setTab={(t) => triggerHashUpdate(t)} />

      {/* Main Home Index Grid Layout */}
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col space-y-12">
        <Suspense fallback={
          <div className="flex flex-col items-center justify-center py-24 space-y-4">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-slate-200 border-t-[#004a99]"></div>
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Wird geladen...</span>
          </div>
        }>
          {currentPath === '/impressum' && (
            <ImpressumPage onBack={() => navigateTo('/')} />
          )}
          {currentPath === '/datenschutz' && (
            <DatenschutzPage onBack={() => navigateTo('/')} />
          )}
          {currentPath === '/haftungsausschluss' && (
            <DisclaimerPage onBack={() => navigateTo('/')} />
          )}
          {currentPath === '/kontakt' && (
            <KontaktPage onBack={() => navigateTo('/')} />
          )}

          {!isLegalRoute && (
            <>
              {(currentPath === '/abfindungsrechner' || currentPath === '/arbeit-soziales/abfindungsrechner') && (
                <SeveranceCalculator onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/brutto-netto' || currentPath === '/steuerrechner/brutto-netto') && (
                <BruttoNettoCalculator onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(
                currentPath === '/bruttorechner' ||
                currentPath === '/brutto-rechner' ||
                currentPath === '/steuerrechner/bruttorechner' ||
                currentPath === '/steuerrechner/brutto-rechner'
              ) && (
                <BruttoRechnerCalculator onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/einkommensteuer' || currentPath === '/steuerrechner/einkommensteuer') && (
                <EinkommensteuerCalculator onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/lohnsteuerrechner' || currentPath === '/steuerrechner/lohnsteuerrechner') && (
                <LohnsteuerCalculator onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/nettopreis-rechner' || currentPath === '/steuerrechner/nettopreis-rechner') && (
                <NettopreisCalculator onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(
                currentPath === '/abgeltungsteuerrechner' ||
                currentPath === '/abgeltungssteuer-rechner' ||
                currentPath === '/steuerrechner/abgeltungsteuerrechner' ||
                currentPath === '/steuerrechner/abgeltungssteuer-rechner' ||
                currentPath === '/kapitalertragsteuer-rechner' ||
                currentPath === '/steuerrechner/kapitalertragsteuer-rechner'
              ) && (
                <AbgeltungssteuerCalculator onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(
                currentPath === '/aussergewoehnliche-belastungen' ||
                currentPath === '/steuerrechner/aussergewoehnliche-belastungen' ||
                currentPath === '/aussergewoehnliche-belastungen-rechner' ||
                currentPath === '/steuerrechner/aussergewoehnliche-belastungen-rechner'
              ) && (
                <AussergewoehnlicheBelastungenCalculator onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/steuer-berechnen' || currentPath === '/steuerrechner/steuer-berechnen') && (
                <SteuerrechnerCalculator onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/erbschaftsteuer-rechner' || currentPath === '/erbschaftsteuerrechner' || currentPath === '/steuerrechner/erbschaftsteuer-rechner') && (
                <ErbschaftsteuerCalculator onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/erbfolge-rechner' || currentPath === '/erbfolgerechner' || currentPath === '/recht/erbfolge-rechner' || currentPath === '/steuerrechner/erbfolge-rechner' || currentPath === '/familie/erbfolge-rechner') && (
                <ErbfolgeCalculator onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/arbeitslosengeld-rechner' || currentPath === '/arbeit-soziales/arbeitslosengeld-rechner') && (
                <ArbeitslosengeldCalculator onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/bafoeg-rechner' || currentPath === '/arbeit-soziales/bafoeg-rechner') && (
                <BafoegCalculator onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/wohngeldrechner' || currentPath === '/arbeit-soziales/wohngeldrechner') && (
                <WohngeldCalculator onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/buergergeld-rechner' || currentPath === '/arbeit-soziales/buergergeld-rechner') && (
                <BuergergeldCalculator onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/elterngeld-rechner' || currentPath === '/arbeit-soziales/elterngeld-rechner') && (
                <ElterngeldCalculator onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/elternzeitrechner' || currentPath === '/arbeit-soziales/elternzeitrechner') && (
                <Elternzeitrechner onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/arbeitszeitrechner' || currentPath === '/arbeit-soziales/arbeitszeitrechner' || currentPath === '/zeit-datum/arbeitszeitrechner') && (
                <Arbeitszeitrechner onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/altersrechner' || currentPath === '/zeit-datum/altersrechner' || currentPath === '/alters-rechner') && (
                <AltersrechnerCalculator onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/arbeitstage-rechner' || currentPath === '/arbeitstagerechner' || currentPath === '/zeit-datum/arbeitstage-rechner' || currentPath === '/zeit-datum/arbeitstagerechner') && (
                <ArbeitstageRechner onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/zeitrechner' || currentPath === '/zeit-datum/zeitrechner') && (
                <ZeitrechnerCalculator onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/datumsrechner' || currentPath === '/zeit-datum/datumsrechner') && (
                <DatumsrechnerCalculator onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/et-rechner' || currentPath === '/gesundheit/et-rechner' || currentPath === '/familie/et-rechner' || currentPath === '/geburtstermin' || currentPath === '/geburtsterminrechner') && (
                <EtRechner onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/ssw-rechner' || currentPath === '/gesundheit/ssw-rechner' || currentPath === '/familie/ssw-rechner' || currentPath === '/et-rechner-ssw' || currentPath === '/schwangerschaftswochen-rechner') && (
                <SswRechner onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/et-rechner-felgen' || currentPath === '/umrechner/et-rechner-felgen' || currentPath === '/kfz/et-rechner-felgen' || currentPath === '/felgenrechner' || currentPath === '/einpresstiefe-rechner') && (
                <EtFelgenRechner onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/restwert-rechner' || currentPath === '/kredit-finanzierung/restwert-rechner' || currentPath === '/kfz/restwert-rechner' || currentPath === '/auto-wertverlust-rechner' || currentPath === '/fahrzeugwert-rechner' || currentPath === '/autowert-rechner') && (
                <RestwertRechner onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/eisprungrechner' || currentPath === '/gesundheit/eisprungrechner' || currentPath === '/familie/eisprungrechner' || currentPath === '/et-rechner-eisprung' || currentPath === '/ovulationsrechner') && (
                <EisprungRechner onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/laengenumrechner' || currentPath === '/umrechner/laengenumrechner') && (
                <Laengenumrechner onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/feiertage-2026' && (
                <Feiertage2026 onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/feiertage-in-deutschland' && (
                <FeiertageInDeutschland onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/feiertage-2027' && (
                <Feiertage2027 onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/feiertage' || currentPath === '/feiertage/') && (
                <FeiertageAll onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/feiertage-2025' && (
                <Feiertage2025 onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/feiertage-nrw-2026' && (
                <FeiertageNrw2026 onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/feiertage-2026-nrw' && (
                <Feiertage2026Nrw onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/nrw-feiertage-2026' && (
                <NrwFeiertage2026 onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/feiertage-nrw-2025' && (
                <FeiertageNrw2025 onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/feiertage-bayern-2026' && (
                <FeiertageBayern2026 onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/feiertage-bayern-2025' && (
                <FeiertageBayern2025 onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/feiertage-bw-2026' && (
                <FeiertageBw2026 onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/feiertage-baden-wuerttemberg-2026' && (
                <FeiertageBadenWuerttemberg2026 onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/feiertage-hessen-2026' && (
                <FeiertageHessen2026 onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/feiertage-niedersachsen-2026' && (
                <FeiertageNiedersachsen2026 onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/feiertage-berlin-2026' && (
                <FeiertageBerlin2026 onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/feiertage-sachsen-2026' && (
                <FeiertageSachsen2026 onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/feiertage-sachsen-2025' && (
                <FeiertageSachsen2025 onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/feiertage-rlp-2026' && (
                <FeiertageRlp2026 onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/feiertage-hamburg-2026' && (
                <FeiertageHamburg2026 onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/feiertage-mai-2026' && (
                <FeiertageMai2026 onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {currentPath === '/brueckentage-planer' && (
                <BrueckentagePlaner onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/waermepumpe' || currentPath === '/waermepumpen-rechner' || currentPath === '/energie/waermepumpe') && (
                <WaermepumpenRechner onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/gasverbrauch' || currentPath === '/gasverbrauchsrechner' || currentPath === '/energie/gasverbrauch') && (
                <Gasverbrauchsrechner onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/stromverbrauch' || currentPath === '/stromverbrauchsrechner' || currentPath === '/energie/stromverbrauch') && (
                <Stromverbrauchsrechner onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/stromkosten' || currentPath === '/stromkostenrechner' || currentPath === '/energie/stromkosten') && (
                <Stromkostenrechner onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/led-watt' || currentPath === '/led-watt-rechner' || currentPath === '/energie/led-watt') && (
                <LedWattRechner onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(currentPath === '/volt-watt-amp' || currentPath === '/volt-watt-ampere-rechner' || currentPath === '/energie/volt-watt-amp') && (
                <VoltWattAmpereRechner onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(
                currentPath === '/waehrungsrechner' ||
                currentPath === '/waehrungsumrechner' ||
                currentPath === '/waehrungs-rechner' ||
                currentPath === '/geldanlage-sparen/waehrungsrechner' ||
                currentPath === '/waehrungcategory/waehrungsrechner'
              ) && (
                <Waehrungsrechner onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(
                currentPath === '/rechner-einbinden' ||
                currentPath === '/widget-generator' ||
                currentPath === '/rechner-widget' ||
                currentPath === '/widget' ||
                currentPath === '/embed' ||
                currentPath === '/rechner-einbetten'
              ) && (
                <EmbedPage onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(
                currentPath === '/bankverbindung-pruefen' ||
                currentPath === '/bank-pruefen' ||
                currentPath === '/geldanlage-sparen/bank-pruefen' ||
                currentPath === '/geldanlage-sparen/bankverbindung-pruefen'
              ) && (
                <BankverbindungPruefen onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(
                currentPath === '/blz-suche' ||
                currentPath === '/bankleitzahlensuche' ||
                currentPath === '/bankleitzahlen-suche' ||
                currentPath === '/geldanlage-sparen/blz-suche' ||
                currentPath === '/geldanlage-sparen/bankleitzahlensuche'
              ) && (
                <Bankleitzahlensuche onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(
                currentPath === '/etf-sparplanrechner' ||
                currentPath === '/etf-sparplan' ||
                currentPath === '/etf-rechner' ||
                currentPath === '/geldanlage-sparen/etf-sparplan' ||
                currentPath === '/geldanlage-sparen/etf-sparplanrechner'
              ) && (
                <EtfSparplanrechner onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(
                currentPath === '/entnahmeplanrechner' ||
                currentPath === '/entnahmeplan' ||
                currentPath === '/etf-entnahmeplan' ||
                currentPath === '/geldanlage-sparen/entnahmeplan' ||
                currentPath === '/geldanlage-sparen/entnahmeplanrechner'
              ) && (
                <Entnahmeplanrechner />
              )}

              {(
                currentPath === '/bankrecherche' ||
                currentPath === '/banksuche' ||
                currentPath === '/bank-suche' ||
                currentPath === '/geldanlage-sparen/bankrecherche' ||
                currentPath === '/geldanlage-sparen/banksuche'
              ) && (
                <Bankrecherche />
              )}

              {(
                currentPath === '/iban-rechner' ||
                currentPath === '/iban-berechnen' ||
                currentPath === '/iban-generator' ||
                currentPath === '/geldanlage-sparen/iban-rechner' ||
                currentPath === '/geldanlage-sparen/iban-berechnen'
              ) && (
                <IbanRechner />
              )}

              {(
                currentPath === '/bmi-rechner' ||
                currentPath === '/gesundheit/bmi-rechner' ||
                currentPath === '/bmi'
              ) && (
                <BmiRechner onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {(
                currentPath === '/bmi-rechner-kinder' ||
                currentPath === '/bmi-kinder' ||
                currentPath === '/gesundheit/bmi-rechner-kinder' ||
                currentPath === '/gesundheit/bmi-kinder'
              ) && (
                <BmiKinderRechner onBackToPortal={() => navigateTo('/')} navigateTo={navigateTo} />
              )}

              {!isCalculatorRoute && isHomeRoute && (
                <>
                  {/* Dynamic Premium Header matching exact spec */}
                  <section className="bg-gradient-to-br from-[#004a99] via-[#003d7e] to-slate-950 rounded-3xl text-white px-6 py-10 sm:p-12 lg:p-16 shadow-xl relative" id="portal-hero">
            {/* Subtle grid pattern background */}
            <div className="absolute inset-0 rounded-3xl opacity-[0.04] pointer-events-none bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:16px_16px]"></div>
            
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center max-w-6xl mx-auto">
              {/* Left Column: Title, Sub, Search */}
              <div className="lg:col-span-7 space-y-6 text-left">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/10 px-4 py-1.5 text-xs font-bold text-blue-300 border border-blue-400/20 uppercase tracking-widest">
                  smart-rechnr.de
                </span>
                <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight font-display leading-[1.1] text-white">
                  {"\u00DCber 200+ Online-Rechner"}
                </h1>
                <p className="text-sm sm:text-base text-blue-100 leading-relaxed max-w-2xl">
                  {"Finden Sie schnell und unkompliziert pr\u00E4zise steuerliche Berechnungen, verl\u00E4ssliche Finanzanalysen und offizielle gesetzssichere Formulare."}
                </p>

                {/* Instant Real-time Calculator Search */}
                <div ref={searchContainerRef} className="relative max-w-xl pt-2" id="live-search-container">
                  <div className="relative">
                    <Search className="absolute left-4 top-3.5 h-5 w-5 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Rechner suchen... (z. B. Gehalt, Abfindung, Zins...)"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setIsSearchOpen(true);
                      }}
                      onFocus={() => {
                        if (searchQuery.trim()) {
                          setIsSearchOpen(true);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setIsSearchOpen(false);
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                      className="w-full pl-12 pr-10 py-3.5 bg-white text-slate-800 font-medium placeholder-slate-400 rounded-2xl shadow-lg border border-slate-100 focus:outline-none focus:ring-4 focus:ring-blue-500/30 text-sm transition"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchQuery('');
                          setIsSearchOpen(false);
                        }}
                        className="absolute right-3.5 top-3.5 p-0.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition cursor-pointer"
                        title="Suche zurücksetzen"
                        aria-label="Suche zurücksetzen"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* Popular query suggestion chips */}
                  <div className="flex flex-wrap gap-1.5 mt-2.5 text-xs items-center" id="search-keyword-suggestions">
                    <span className="text-blue-200/85 font-extrabold text-[10px] uppercase tracking-wider mr-1">{"Vorschläge:"}</span>
                    {[
                      { label: "Feiertage 2026", term: "Feiertage 2026" },
                      { label: "Feiertage 2027", term: "Feiertage 2027" },
                      { label: "Feiertage NRW", term: "Feiertage NRW" },
                      { label: "Brückentage", term: "Brückentage" },
                      { label: "Brutto-Netto", term: "Brutto-Netto" },
                      { label: "Einkommensteuer", term: "Einkommensteuer" },
                      { label: "Abfindung", term: "Abfindung" },
                      { label: "Zinsen", term: "Zins" }
                  ].map((chip) => (
                    <button
                      key={chip.label}
                      onClick={() => {
                        setSearchQuery(chip.term);
                        setIsSearchOpen(true);
                      }}
                      className="bg-white/10 hover:bg-white/20 text-white border border-white/10 rounded-lg px-2.5 py-1 text-[11px] font-bold transition hover:scale-[1.04] active:scale-95 cursor-pointer shadow-sm"
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>

                {/* Instant Search Results Panel */}
                {isSearchOpen && searchQuery.trim() && (
                  <div 
                    className="absolute left-0 right-0 mt-3 bg-white rounded-2xl shadow-2xl border border-slate-100 text-left p-3 z-50 text-slate-800 max-h-96 overflow-y-auto"
                  >
                    <div className="text-[10px] uppercase font-bold text-slate-400 px-3 pb-2 tracking-wider flex justify-between items-center">
                      <span>{"Suchergebnisse"} ({searchResults.length})</span>
                      <span className="text-[9px] text-blue-500 font-extrabold lowercase bg-blue-50 px-1.5 py-0.5 rounded">{"Intelligenter Abgleich aktiv"}</span>
                    </div>
                    {searchResults.length > 0 ? (
                      <div className="space-y-0.5">
                        {searchResults.map(({ calc, boldIndices }, i) => {
                          const isImpl = !!calc.isImplemented;
                          const cleanUrl = isImpl ? (calc.path.startsWith('#') ? calc.path.substring(1) : calc.path) : '#';
                          return (
                            <a
                              key={i}
                              href={cleanUrl}
                              onClick={(e) => {
                                e.preventDefault();
                                setIsSearchOpen(false);
                                if (isImpl) {
                                  navigateTo(calc.path);
                                }
                              }}
                              className="flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-slate-50 transition"
                            >
                              <div>
                                {/* Dynamic character highlighting */}
                                <div className="font-bold text-xs text-slate-800">
                                  {calc.name.split("").map((char, idx) => {
                                    const isHighlighted = boldIndices.has(idx);
                                    return (
                                      <span 
                                        key={idx} 
                                        className={isHighlighted ? "bg-blue-100 text-[#004a99] font-black rounded-sm px-0.5" : ""}
                                      >
                                        {char}
                                      </span>
                                    );
                                  })}
                                </div>
                                <div className="text-[10px] text-slate-400 font-semibold">{calc.categoryTitle}</div>
                              </div>
                              <span className="inline-flex items-center space-x-1">
                                {calc.isImplemented ? (
                                  <span className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded">{"Bereit"}</span>
                                ) : (
                                  <span className="text-[10px] font-semibold text-slate-400">{"Externer Link"}</span>
                                )}
                                <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                              </span>
                            </a>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-xs text-slate-500 text-center py-6 font-semibold">
                        {"Kein Rechner gefunden f\u00FCr"} "{searchQuery}"
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Hero Graphic Illustration */}
            <div className="lg:col-span-5 flex justify-center items-center mt-6 lg:mt-0">
              <div
                className="relative group w-full max-w-sm lg:max-w-none"
              >
                {/* Visual glow backdrop frame behind illustration */}
                <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-blue-500 to-teal-400 opacity-25 blur-2xl group-hover:opacity-35 transition duration-1000"></div>
                {/* 100% Transparent Wrapper Container */}
                <div className="relative bg-transparent overflow-hidden aspect-[4/3] flex items-center justify-center w-full">
                  <SmartRechnrIllustration />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Methodik & Verifizierte Datengrundlagen */}
        <section className="bg-white rounded-3xl border border-slate-200/80 p-8 shadow-sm space-y-6" id="methodology-section">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 space-y-3">
              <span className="text-[10px] font-bold text-[#004a99] bg-blue-50 px-3 py-1 rounded-full uppercase tracking-widest">
                {"Pr\u00E4zision & Transparenz"}
              </span>
              <h2 className="text-xl sm:text-2xl font-extrabold text-slate-800 tracking-tight leading-snug font-display">
                {"Offizielle Berechnungsmethoden"}
              </h2>
              <p className="text-xs text-slate-500 leading-relaxed">
                {"Unsere Rechenmodelle basieren auf den amtlichen Vorgaben der Bundesministerien, gesetzlichen Tarifen und aktuellen Beitragsbemessungsgrenzen. Alle Berechnungen erfolgen transparent und nachvollziehbar."}
              </p>
            </div>

            {/* Grid of official legal sources and verified methodologies */}
            <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { name: 'BMF PAP 2026/2027', sub: 'Offizieller Lohnsteuertarif' },
                { name: '\u00A7 32a EStG', sub: 'Tarifzonen & Grundfreibetrag' },
                { name: 'Sozialversicherungs-BBG', sub: 'KV, RV, PV & AV S\u00E4tze' },
                { name: 'WoGG & Mietstufen', sub: 'Wohngeld-Plus Berechnungen' },
                { name: '\u00A7 187 ff. BGB', sub: 'Gesetzliche Fristenberechnung' },
                { name: 'BEEG Richtlinien', sub: 'Bundeselterngeldgesetz' },
                { name: 'Bundesweiter Stromspiegel', sub: 'Haushalts-Referenzwerte' },
                { name: '100% Lokale Ausf\u00FChrung', sub: 'Keine Speicherung im Web' }
              ].map((source, idx) => (
                <div key={idx} className="bg-slate-50 border border-slate-100 rounded-xl p-3.5 flex flex-col items-center justify-center text-center transition hover:border-[#004a99]/20 hover:bg-white">
                  <span className="text-slate-800 font-extrabold text-xs tracking-tight">{source.name}</span>
                  <span className="text-[9px] font-bold text-[#004a99] uppercase tracking-widest mt-0.5 opacity-80">{source.sub}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Highlight Feature Banner: Rechner für Ihre Website einbinden (Kostenloses Widget-Tool) */}
        <section className="bg-gradient-to-r from-blue-900 via-[#004a99] to-indigo-900 rounded-3xl p-6 sm:p-8 text-white shadow-lg relative overflow-hidden" id="widget-promo-banner">
          <div className="absolute right-0 top-0 bottom-0 w-1/3 opacity-10 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:12px_12px] pointer-events-none"></div>
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="space-y-2 max-w-2xl text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-400/20 text-blue-200 border border-blue-300/30 text-[11px] font-bold uppercase tracking-wider">
                <Code className="w-3.5 h-3.5 text-blue-300" />
                <span>Neu für Webmaster &amp; Blogger</span>
              </div>
              <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight font-display text-white">
                Rechner kostenlos in Ihre eigene Website einbinden
              </h2>
              <p className="text-xs sm:text-sm text-blue-100 leading-relaxed">
                Generieren Sie in 10 Sekunden einen responsiven iFrame- oder WordPress-Code für Brutto-Netto, Lohnsteuer, Währungen, Stromkosten &amp; Co. Bieten Sie Ihren Lesern echten Mehrwert &amp; erhöhen Sie Ihre Verweildauer.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
              <a
                href="/rechner-einbinden"
                onClick={(e) => {
                  e.preventDefault();
                  navigateTo('/rechner-einbinden');
                }}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-white text-[#004a99] hover:bg-blue-50 font-extrabold text-xs sm:text-sm shadow-md transition hover:scale-[1.02] active:scale-95 cursor-pointer"
              >
                <span>Widget-Generator öffnen</span>
                <ArrowRight className="w-4 h-4 text-[#004a99]" />
              </a>
            </div>
          </div>
        </section>

        {/* Dynamic Categorized Grids with Independent Slider Controls */}
        <section className="space-y-6" id="calculators-index">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-2 border-b border-slate-200 pb-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-extrabold text-slate-800 tracking-tight font-display">
                Entdecken Sie alle Branchen-Rechner
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                {"Klicken Sie auf einen Rechner, um die jeweilige Erfassungsmaske direkt zu \u00F6ffnen."}
              </p>
            </div>
            <div className="flex items-center space-x-2 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
              <span>{"Schnelle \u00DCbersicht"}</span>
              <Layers className="h-4 w-4 text-slate-400" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" id="categories-deck">
            {CATEGORIES_DATA.map((group) => {
              const isFeiertageGroup = group.id === 'feiertage-2027';
              const filteredCalcs = isFeiertageGroup && feiertageSiloTab !== 'all'
                ? group.calculators.filter((c) => c.siloGroup === feiertageSiloTab)
                : group.calculators;
              const totalItems = filteredCalcs.length;
              const sliderIndex = isFeiertageGroup && feiertageSiloTab !== 'all' 
                ? (categorySliders[`${group.id}-${feiertageSiloTab}`] || 0)
                : (categorySliders[group.id] || 0);
              const limit = group.id === 'arbeit-soziales' || group.id === 'steuerrechner' ? 8 : (isFeiertageGroup ? 6 : 7);
              const visibleCalcs = filteredCalcs.slice(sliderIndex, sliderIndex + limit);
              const sliderKey = isFeiertageGroup && feiertageSiloTab !== 'all' 
                ? `${group.id}-${feiertageSiloTab}` 
                : group.id;

              return (
                <div key={group.id} className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm space-y-3 hover:shadow-md transition duration-200 flex flex-col justify-between" id={`group-card-${group.id}`}>
                  <div>
                    {/* Header with Title and count */}
                    <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                      <div>
                        <h3 className="font-extrabold text-[#004a99] text-sm font-display tracking-tight leading-tight uppercase flex items-center gap-1.5">
                          {group.title}
                        </h3>
                        <span className="text-[10px] text-slate-400 font-semibold">{group.englishTitle}</span>
                      </div>
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600 font-mono">
                        {group.count} Rechner
                      </span>
                    </div>

                    {/* Silo Navigation for Feiertage */}
                    {isFeiertageGroup && (
                      <div className="pt-2.5 pb-1 border-b border-slate-100/80">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                            Silo-Navigation
                          </span>
                          <span className="text-[10px] text-[#004a99] font-semibold">
                            {feiertageSiloTab === 'all' ? 'Alle 19 Feiertagsseiten' : feiertageSiloTab === 'jahr' ? 'Jahre (2025–2027)' : feiertageSiloTab === 'bundesland' ? 'Bundesländer (11 Seiten)' : 'Spezial-Planer (2)'}
                          </span>
                        </div>
                        <div className="grid grid-cols-4 gap-1 p-0.5 bg-slate-100/90 rounded-lg text-center">
                          <button
                            type="button"
                            onClick={() => setFeiertageSiloTab('all')}
                            className={`py-1 px-1 rounded-md text-[10px] font-bold transition cursor-pointer ${feiertageSiloTab === 'all' ? 'bg-white text-[#004a99] shadow-xs' : 'text-slate-600 hover:text-slate-900'}`}
                          >
                            Alle
                          </button>
                          <button
                            type="button"
                            onClick={() => setFeiertageSiloTab('jahr')}
                            className={`py-1 px-1 rounded-md text-[10px] font-bold transition cursor-pointer ${feiertageSiloTab === 'jahr' ? 'bg-white text-[#004a99] shadow-xs' : 'text-slate-600 hover:text-slate-900'}`}
                          >
                            Jahre
                          </button>
                          <button
                            type="button"
                            onClick={() => setFeiertageSiloTab('bundesland')}
                            className={`py-1 px-1 rounded-md text-[10px] font-bold transition cursor-pointer ${feiertageSiloTab === 'bundesland' ? 'bg-white text-[#004a99] shadow-xs' : 'text-slate-600 hover:text-slate-900'}`}
                          >
                            Länder
                          </button>
                          <button
                            type="button"
                            onClick={() => setFeiertageSiloTab('spezial')}
                            className={`py-1 px-1 rounded-md text-[10px] font-bold transition cursor-pointer ${feiertageSiloTab === 'spezial' ? 'bg-white text-[#004a99] shadow-xs' : 'text-slate-600 hover:text-slate-900'}`}
                          >
                            Planer
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Interactive List Carousel Grid block */}
                    <div className="pt-3 flex-1 flex flex-col justify-between">
                      <div className="space-y-1.5">
                        {visibleCalcs.map((calc) => {
                          const isImpl = !!calc.isImplemented;
                          const cleanUrl = isImpl ? (calc.path.startsWith('#') ? calc.path.substring(1) : calc.path) : '#';
                          return (
                            <a
                              key={calc.name}
                              href={cleanUrl}
                              onClick={(e) => {
                                e.preventDefault();
                                if (isImpl) {
                                  navigateTo(calc.path);
                                }
                              }}
                              title={calc.name}
                              className="relative flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 transition border border-transparent hover:border-slate-100"
                            >
                              <div className="min-w-0 pr-2">
                                <span className="text-xs text-slate-700 font-medium hover:text-[#004a99] block truncate">
                                  {calc.name}
                                </span>
                              </div>

                              <span className="shrink-0 flex items-center space-x-1">
                                {calc.siloGroup && isFeiertageGroup && (
                                  <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded leading-none ${
                                    calc.siloGroup === 'jahr' 
                                      ? 'bg-blue-50 text-blue-700' 
                                      : calc.siloGroup === 'bundesland'
                                      ? 'bg-amber-50 text-amber-800'
                                      : 'bg-emerald-50 text-emerald-800'
                                  }`}>
                                    {calc.siloGroup === 'jahr' ? 'Jahr' : calc.siloGroup === 'bundesland' ? 'Land' : 'Planer'}
                                  </span>
                                )}
                                {calc.isImplemented ? (
                                  <span className="text-[9px] font-bold text-green-700 bg-green-50 px-1.5 py-0.5 rounded leading-none">Aktiv</span>
                                ) : (
                                  <span className="text-[9px] font-semibold text-slate-400 bg-slate-50 px-1 py-0.5 rounded leading-none">Extern</span>
                                )}
                                <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                              </span>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Slider controls at the bottom */}
                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <span className="text-[10px] text-slate-400 font-medium tracking-wide">
                      Anzeige {totalItems > 0 ? sliderIndex + 1 : 0} - {Math.min(sliderIndex + limit, totalItems)} von {totalItems}
                    </span>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handlePrevSlide(sliderKey, limit, totalItems)}
                        disabled={totalItems <= limit}
                        className={`p-1 rounded-md border border-slate-200 text-slate-500 transition ${totalItems <= limit ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50 cursor-pointer'}`}
                        title="Vorherige Rechner"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleNextSlide(sliderKey, limit, totalItems)}
                        disabled={totalItems <= limit}
                        className={`p-1 rounded-md border border-slate-200 text-slate-500 transition ${totalItems <= limit ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50 cursor-pointer'}`}
                        title="Nächste Rechner"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Highlights Section featuring guides, infocenter, and statistics */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-8 pt-6 border-t border-slate-200" id="highlights">
          
          {/* Column 1: Ratgeber-Highlights */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm flex flex-col justify-between" id="highlights-guide">
            <div>
              <div className="flex items-center space-x-2 border-b border-slate-100 pb-3 mb-4">
                <BookOpen className="h-5 w-5 text-blue-600" />
                <h3 className="font-extrabold text-slate-800 text-sm font-display tracking-tight uppercase">
                  Ratgeber-Highlights
                </h3>
              </div>
              <p className="text-xs text-slate-400 mb-4 font-semibold pb-1">{"Umfangreiches Praxiswissen und verst\u00E4ndliche Tipps:"}</p>
              
              <div className="space-y-4 max-h-[480px] overflow-y-auto pr-1 no-scrollbar">
                {RATGEBER_HIGHLIGHTS.map((guide, idx) => (
                  <div key={idx} className="group border-b border-slate-50 pb-3 last:border-0 hover:bg-slate-50/40 p-1.5 rounded-lg transition">
                    <span className="text-[10px] font-bold text-blue-600 tracking-wider uppercase block mb-0.5">{guide.category}</span>
                    <h4 className="text-xs font-bold text-slate-700 leading-snug group-hover:text-[#004a99] transition">
                      {guide.title}
                    </h4>
                    <span className="text-[10px] text-slate-400 mt-1 block">Bildbezeichnung: {guide.imageAlt}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="border-t border-slate-100 pt-3 mt-4 text-center">
              <span className="text-[10px] text-[#004a99] font-bold uppercase tracking-wider">
                {"18 Ratgeber-Artikel verf\u00FCgbar"}
              </span>
            </div>
          </div>

          {/* Column 2: Infocenter-Highlights */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm flex flex-col justify-between" id="highlights-infocenter">
            <div>
              <div className="flex items-center space-x-2 border-b border-slate-100 pb-3 mb-4">
                <HelpCircle className="h-5 w-5 text-blue-600" />
                <h3 className="font-extrabold text-slate-800 text-sm font-display tracking-tight uppercase">
                  Infocenter-Highlights
                </h3>
              </div>
              <p className="text-xs text-slate-400 mb-4 font-semibold pb-1">{"Verst\u00E4ndliche, thematisch exakte Informationsseiten:"}</p>

              <div className="space-y-4 max-h-[480px] overflow-y-auto pr-1 no-scrollbar">
                {INFOCENTER_HIGHLIGHTS.map((info, idx) => (
                  <div key={idx} className="group border-b border-slate-50 pb-3 last:border-0 hover:bg-slate-50/40 p-1.5 rounded-lg transition">
                    <span className="text-[10px] font-bold text-blue-600 tracking-wider uppercase block mb-0.5">{info.category}</span>
                    <h4 className="text-xs font-bold text-slate-700 leading-snug group-hover:text-[#004a99] transition">
                      {info.title}
                    </h4>
                    <span className="text-[10px] text-slate-400 mt-1 block">Bildbezeichnung: {info.imageAlt}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-slate-100 pt-3 mt-4 text-center">
              <span className="text-[10px] text-[#004a99] font-bold uppercase tracking-wider">
                {"20 Themenseiten verf\u00FCgbar"}
              </span>
            </div>
          </div>

          {/* Column 3: Statistik-Highlights */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm flex flex-col justify-between" id="highlights-statistics">
            <div>
              <div className="flex items-center space-x-2 border-b border-slate-100 pb-3 mb-4">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                <h3 className="font-extrabold text-slate-800 text-sm font-display tracking-tight uppercase">
                  Statistik-Highlights
                </h3>
              </div>
              <p className="text-xs text-slate-400 mb-4 font-semibold pb-1">Entwicklungen und Trends auf anonymisierter Basis:</p>

              <div className="space-y-4 max-h-[480px] overflow-y-auto pr-1 no-scrollbar">
                {STATISTIK_HIGHLIGHTS.map((stat, idx) => (
                  <div key={idx} className="group border-b border-slate-50 pb-3 last:border-0 hover:bg-slate-50/40 p-1.5 rounded-lg transition">
                    <span className="text-[10px] font-bold text-blue-600 tracking-wider uppercase block mb-0.5">{stat.category}</span>
                    <h4 className="text-xs font-bold text-slate-700 leading-snug group-hover:text-[#004a99] transition">
                      {stat.title}
                    </h4>
                    <span className="text-[10px] text-slate-400 mt-1 block">Bildbezeichnung: {stat.imageAlt}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-slate-100 pt-3 mt-4 text-center">
              <span className="text-[10px] text-[#004a99] font-bold uppercase tracking-wider">
                Statistik-Analysen 2026 live
              </span>
            </div>
          </div>

        </section>

        {/* TOP CALCULATORS Section */}
        <section className="bg-white rounded-3xl border border-slate-200/80 p-8 shadow-sm space-y-6" id="top-calculators">
          <div className="border-b border-slate-100 pb-3">
            <h2 className="text-lg font-extrabold text-slate-800 tracking-tight leading-snug font-display uppercase">
              Unsere Top-Rechner
            </h2>
            <p className="text-xs text-slate-400">{"Direkter Einstieg in die meistgenutzten Formulare der Bundesb\u00FCrger."}</p>
          </div>

          <div className="flex flex-wrap gap-2">
            {TOP_CALCULATORS.map((calc, i) => {
              const matched = allCalculatorsList.find(c => c.name === calc.name || c.path === calc.path);
              const isImpl = !!(matched && matched.isImplemented);
              const cleanUrl = isImpl ? (calc.path.startsWith('#') ? calc.path.substring(1) : calc.path) : '#';
              return (
                <a
                  key={i}
                  href={cleanUrl}
                  onClick={(e) => {
                    e.preventDefault();
                    if (isImpl) {
                      navigateTo(calc.path);
                    }
                  }}
                  className={`py-2 px-3.5 rounded-xl text-xs font-semibold border transition flex items-center gap-1.5 ${
                    isImpl 
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:border-emerald-400 hover:bg-emerald-100/80 shadow-sm font-bold' 
                      : 'bg-white text-slate-500 border-slate-200/80 hover:border-slate-300'
                  }`}
                >
                  {isImpl && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />}
                  <span>{calc.name}</span>
                  {isImpl && (
                    <span className="text-[9px] px-1 bg-emerald-800 text-white rounded font-extrabold uppercase tracking-wide">
                      {"Aktiv"}
                    </span>
                  )}
                </a>
              );
            })}
          </div>
        </section>

        {/* Transparenz, Datenschutz, Methodik & E-E-A-T Experten-Redaktion */}
        <section className="bg-slate-50 border border-slate-200/80 rounded-3xl p-6 sm:p-8 space-y-6 max-w-4xl mx-auto shadow-xs" id="corporate-intro">
          <div className="text-center space-y-3">
            <span className="text-[10px] font-bold text-[#004a99] bg-blue-100/60 px-3 py-1 rounded-full uppercase tracking-widest">
              Wissenschaftliche Standards &amp; E-E-A-T
            </span>
            <h2 className="text-xl sm:text-2xl font-extrabold text-slate-800 font-display">
              Transparente Berechnungsmethodik &amp; Geprüfte Finanzmathematik
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Smart-Rechnr.de bietet kostenlose, mathematisch validierte Online-Rechner für Steuern, Gehalt, Soziales, Geldanlage und Energie. Alle Berechnungen erfolgen vollständig clientseitig in Ihrem Browser: Wir erfassen, speichern und übertragen zu keinem Zeitpunkt persönliche Daten.
            </p>
          </div>

          {/* Author Card: Ahmed, M.Sc. Computer Science & Mathematics */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-center sm:items-start gap-4 text-left">
            <div className="relative w-14 h-14 rounded-2xl overflow-hidden shrink-0 shadow-md bg-gradient-to-br from-[#004a99] to-blue-700">
              <img
                src="/images/ahmed.jpg"
                alt="Ahmed – M.Sc. Computer Science & Mathematics"
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLElement).style.display = 'none';
                  const fallback = (e.target as HTMLElement).nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
              <div 
                className="absolute inset-0 bg-gradient-to-br from-[#004a99] to-blue-700 text-white font-bold text-lg items-center justify-center hidden"
                aria-hidden="true"
              >
                AH
              </div>
            </div>
            <div className="space-y-1.5 flex-1 text-center sm:text-left">
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                <span className="font-extrabold text-slate-900 text-sm sm:text-base">Ahmed</span>
                <span className="text-[11px] font-bold bg-blue-50 text-[#004a99] px-2 py-0.5 rounded-full border border-blue-200">
                  M.Sc. Computer Science &amp; Mathematics
                </span>
                <span className="text-[10px] font-semibold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full border border-emerald-200">
                  Verantwortlicher Chefredakteur &amp; Entwickler
                </span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Spezialisiert auf numerische Algorithmen, Finanzmathematik und komplexe gesetzliche Steuertarife (u.a. BMF PAP, § 32a EStG, EZB-Devisenkursmodelle). Jedes Rechenmodell wird vor der Veröffentlichung nach strengen mathematischen Kontrollverfahren auditiert und laufend an die offizielle Rechtslage angepasst.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 pt-2 text-[10px] text-slate-500 uppercase font-bold tracking-widest border-t border-slate-200/60">
            <span className="flex items-center gap-1.5"><Shield className="h-4 w-4 text-[#004a99]" /> 100% Anonym &amp; DSGVO-konform</span>
            <span className="flex items-center gap-1.5"><Award className="h-4 w-4 text-[#004a99]" /> Amtliche Primärquellen</span>
            <span className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-[#004a99]" /> Kostenlos &amp; Werbefrei</span>
          </div>
        </section>
              </>
            )}

            {!isCalculatorRoute && !isHomeRoute && (
              <NotFound navigateTo={navigateTo} />
            )}
          </>
        )}
        </Suspense>
      </main>

      {/* Trust Badge Footer */}
      <Footer />
    </div>
  );
}
